import { BrokerExecution, BrokerManagerClient, BrokerOrder, BrokerPosition, ChartDataClient, ChartDataRequestModel, ChartIndicatorsClient, ClientResponseModel, Connection, CreatePriceAlertRequestModel, HaltedClient, HaltedRequestModel, HaltedResponseModel, HolidayClient, HolidayModel, PriceAlertClient, PriceAlertResponseModel, ThrottledMarketDataClient, TIUtilities, TopListClient, TopListDataRequestModel, TopListDataResponseModel } from "../../../../pure-javascript-api/ti-js-api";
import { DrawExitOrderComponent } from "../components/draw-exit-order.component";
import { DrawOrderExecutionComponent } from "../components/draw-order-execution.component";
import { DrawTradeOrdersComponent } from "../components/draw-trade-orders.component";
import { InformationPopupComponent } from "../components/information-popup.component";
import { MarketingWebChartXAxisPlotLineComponent } from "../components/marketing-webchart-preview-x-axis-plot-line.component";
import { MarketingWebChartSummaryStatusLineComponent } from "../components/marketing-webchart-summary-status-line";
import { PnlComponent } from "../components/pnl.component";
import { DateHelpers, getHolidayManager, getHolidayManagerInstance } from "../helpers/date-helpers";
import { FormatHelpers } from "../helpers/format-helpers";
import { ObjectHelpers } from "../helpers/object-helpers";
import { RealTimeTradeHelpers } from "../helpers/realtime-trade-helpers";
import { TimeframeHelper } from "../helpers/timeframe-helpers";
import { TraceLoggingHelpers } from "../helpers/trace-logging-helpers";
import { UrlHelpers } from "../helpers/url-helpers";
import { WebChartThemeHelpers } from "../helpers/webchart-theme-helpers";
import { JSApiPointToTiChartDataPointMapper } from "../js-api-mappers/data-point/js-api-point-to-ti-chart-point.mapper";
import { JSApiPointToUpdateTiChartDataPointMapper } from "../js-api-mappers/data-point/js-api-point-to-update-ti-chart-point.mapper";
import { JSApiPriceAlertToLimitAlertMapper } from "../js-api-mappers/price-alert/js-api-price-alert-to-limit-alert.mapper";
import { ChartTimeFrameToTimeFrameMapper } from "../js-api-mappers/timeframe/chart-time-frame-to-time-frame.mapper";
import { TimeFrameToChartTimeFrameMapper } from "../js-api-mappers/timeframe/time-frame-to-chart-time-frame.mapper";
import { BaseChartService } from "../models/base/base-chart-service";
import { IChartContainer } from "../models/base/chart-container.interface";
import { ChartSettingsPopupModel } from "../models/chart-style/chart-settings-popup.model";
import { ChartStyleColorModel } from "../models/chart-style/chart-style-color.model";
import { ChartStyleSettingsModel } from "../models/chart-style/chart-style-settings.model";
import { WebchartThemeManager } from "../models/chart-style/web-chart-theme.manager";
import { CompanyProfileDataModel } from "../models/company-profile/company-profile-data.model";
import { RealtimeTradeDataModel } from "../models/data-point/realtime-trade-data.model";
import { TIChartPointModel } from "../models/data-point/ti-chart-point.model";
import { TIIndicatorDataModel } from "../models/data-point/ti-indicator-data.model";
import { DrawOrderExecutionModel } from "../models/draw-trade-order/draw-order-execution.model";
import { TradeOrderDataModel } from "../models/draw-trade-order/trade-order-data.model";
import { TradePositionDataModel } from "../models/draw-trade-order/trade-position-data.model";
import { DrawingToolsSettingModel } from "../models/drawing-tools/drawing-tools-setting.model";
import { EmojiModel } from "../models/drawing-tools/emoji.model";
import { DrawEarningNotificationDataModel } from "../models/earning/draw-earning-notification-data.model";
import { EMACloudSettingModel, EMACloudSettingsModel } from "../models/ema-cloud/ema-cloud-settings.model";
import { ChartModeStyle } from "../models/enum/chart-mode-style.enum";
import { ChartType } from "../models/enum/chart-type.enum";
import { IndicatorType } from "../models/enum/indicator-type.enum";
import { PreviewModeLabelPosition } from "../models/enum/preview-mode-label-position.enum";
import { ScrollMode } from "../models/enum/scroll-mode.enum";
import { Timeframe } from "../models/enum/timeframe.enum";
import { ManageIndicatorModel } from "../models/indicator/manage-indicator.model";
import { WebChartIndicatorManager } from "../models/indicator/web-chart-indicator.manager";
import { CreateLimitAlertModel } from "../models/limit-alert/create-limit-alert.model";
import { LimitAlertModel } from "../models/limit-alert/limit-alert.model";
import { CurrentPositionViewModel } from "../models/pnl/current-position-view-model";
import { SingleStockWindowChangeFromCloseManager } from "../models/single-stock-window/single-stock-window-change-from-close.manager";
import { SingleStockWindowDataModel } from "../models/single-stock-window/single-stock-window-data.model";
import { TemporaryStopLossModel } from "../models/stop-loss/temporary-stop-loss.model";
import { TradeAccountModel } from "../models/trade/trade-account.model";
import { QuoteType, TradeInForceType, TradeModel, TradeOrderType } from "../models/trade/trade.model";
import { ChartMode, ChartTheme } from "../models/window-wrapper/marketing-webchart-window-wrapper.model";
const color = require('../library/darken-color.js');
declare const TIJSAPI: any;
declare const window: any;
declare const liveData: any;

const enum InitQueue {
    HOLIDAY = 0,
    MARKET_SCHEDULE = 1
}

export class WebChartDataService extends BaseChartService {
    private symbolLabel = '';
    private companyName = '';

    private lastPoint: TIChartPointModel | null = null;
    private pointFromYesterday: TIChartPointModel | null = null;
    public lastPrice = 0;
    private previewSymbolSizePercent = 40;
    private timeframe = Timeframe.FiveMinute;
    private newestDate: Date | null = null;
    private oldestDate: Date | null = null;

    public connection: Connection;
    private chartDataClient: ChartDataClient;
    private haltedClient: HaltedClient;
    private topListClient: TopListClient;
    private holidayClient: HolidayClient;
    private priceAlertClient: PriceAlertClient;
    private brokerManagerClient: BrokerManagerClient;
    private throttledMarketDataClient: ThrottledMarketDataClient;
    private chartIndicatorsClient: ChartIndicatorsClient;

    public waitForLogin = true;
    public useLoginPopup = false;
    public pnpEnabled = true;
    private onReconnectStartRefresh = true;
    private displayVolumeIndicator = true;
    private displayEMACloudIndicator = false;
    private emaCloudSectionCount = 3;
    private emaCloudSettings: EMACloudSettingsModel | null = null;

    private isPreMarketDisabled = false;
    private isPostMarketDisabled = false;
    private showPreMarket = true;
    private showPostMarket = true;
    private preMarketMinutes = 330;
    private postMarketMinutes = 300;

    private trace = false;
    public isPnP = false;
    private previewChartBackgroundTrasparentMode = false;
    private chartType = ChartType.Candlestick;

    private chartStyleModel = new ChartStyleSettingsModel();
    private theme = ChartTheme.Light;
    private previewChartLabelPosition = PreviewModeLabelPosition.InsideTopLeft;
    public chartMode = ChartMode.Standard;
    public chartModeStyle = ChartModeStyle.Normal;

    private baseElement: HTMLElement;

    private startingRequestTime: Date | null = null;

    private chartTraceElement: HTMLElement | null = null;

    private mainChartZoomLevel = -5;
    private pipChartZoomLevel = -11;
    // private topListMostRecentClose = 0;

    private indicatorManager: WebChartIndicatorManager;
    private singleStockWindowModel = new SingleStockWindowDataModel();

    public username = '';
    public symbollookup = 'jqueryui';
    public symbolLookupFocusOnLoadQuery = false;
    public requestingInitData = false;
    public switchingSymbol = false;
    public sswNoBackground = false;
    public sswBackgroundPercent = 3;

    public sswSeparatorDarkColor = '#444444';
    public sswSeparatorLightColor = '#bcbcbc';

    //public cfcUpColorLight = '#269C26';  // Green from TiWebsite ticker up color
    public cfcUpColorLight = '#269C26';
    public cfcDownColorLight = '';
    public cfcUpColorDark = '';
    public cfcDownColorDark = '';

    public previewChartLightThemeUpColor = '#269C26';
    public previewChartLightThemeDownColor = '';

    public lastPriceUpColorLight = '#269C26';
    public lastPriceDownColorLight = '';
    public lastPriceUpColorDark = '';
    public lastPriceDownColorDark = '';

    private localStorageDrawingToolsKey = '';
    private localStorageDrawingToolsSettings = '';

    private futureScrollingScrollStopEnabled = false;
    private futureScrollingEnabled = true;
    private futureScrollingBarCount = 30;
    private futureScrollingMinDisplayBarCount = 5;
    private rightEdgePaddingBarsDefault = 5;

    private exitOrderDisplayBluePlotline = true;

    private mobileZoomEnabled = true;
    private marketingChartTimeframeAutoExpand = false;
    private zoomEnabled = false;
    private saveRightAddPadding = true;
    private pipChangePercentEnabled = true;
    private crosshairEnabled = true;
    private newTradeUIEnabled = true;
    private emaCloudEnabled = true;
    private priceAlertAfterHours = true;
    private papertTradeConnected = false;
    private persistPositionShareQuantity = true;

    private zoomAndScrollEnabled = true;
    private zoomAndScrollAbsoluteHeight = 36;

    private yAxisPanningEnabled = true;

    private infoPopup: InformationPopupComponent | null = null;

    private initQueues = [InitQueue.HOLIDAY, InitQueue.MARKET_SCHEDULE];

    constructor(base: IChartContainer, baseElement: HTMLElement) {
        super(base);
        this.baseElement = baseElement;
        this.isPnP = this.base.pnpMode;

        this.indicatorManager = new WebChartIndicatorManager(this);
        this.base.scrollingEnabled = false;
        this.base.scrollMode = ScrollMode.ClickAndDrag;
        this.infoPopup = base.getComponentsByType(InformationPopupComponent)?.[0] as InformationPopupComponent ?? null;

        if (typeof TIJSAPI != 'undefined' && TIJSAPI) {
            TraceLoggingHelpers.log(`${this.isPnP ? 'PIP' : ''} Chart is using TIJSAPI`)
            this.connection = TIJSAPI.Connection.getInstance();
            this.chartDataClient = this.connection.chartDataClient;
            this.topListClient = this.connection.topListClient;
            this.holidayClient = this.connection.holidayClient;
            this.priceAlertClient = this.connection.priceAlertClient;
            this.brokerManagerClient = this.connection.brokerManagerClient;
            this.haltedClient = this.connection.haltedClient;
            this.throttledMarketDataClient = this.connection.throttledMarketDataClient;
            this.chartIndicatorsClient = this.connection.chartIndicatorsClient;
        } else {
            this.connection = Connection.getInstance();
            this.chartDataClient = this.connection.chartDataClient;
            this.topListClient = this.connection.topListClient;
            this.priceAlertClient = this.connection.priceAlertClient;
            this.brokerManagerClient = this.connection.brokerManagerClient;
            this.haltedClient = this.connection.haltedClient;
            this.throttledMarketDataClient = this.connection.throttledMarketDataClient;
            this.chartIndicatorsClient = this.connection.chartIndicatorsClient;
        }

        this.setPropertiesFromQueryStringOrDataAttributes();
        this.onResize();
        this.onReconnected();

        this.getEMACloudSettings();

        this.startingRequestTime = new Date();
        TraceLoggingHelpers.log(`web chart ${this.base.id} subscribeInitChartData`, this.startingRequestTime);

        if (!this.isPnP) {
            this.chartTraceElement = document.querySelector('.' + this.base.id + '-trace');
            this.chartTraceElement?.findFirst('.start span')?.setInnerHtml(DateHelpers.getTraceLoggingTimeStamp());
        } else {
            this.showPreMarket = false;
            this.showPostMarket = false;

            this.chartTraceElement = document.querySelector('.' + this.base.id.replace('-pip', '') + '-trace');
        }
    }

    public override getSymbolLabel(): string {
        if (this.symbolLabel) {
            return this.symbolLabel;
        }

        return this.symbol;
    }

    public getCompanyName(): string {
        return this.companyName;
    }

    public getUsernameKeyStorage(): string {
        let username = this.username?.split(' ').join('').replace(/[^\w\s]/gi, '').toLowerCase() ?? '';
        return username;
    }

    private getPIPKeyStorage(): string {
        const username = this.getUsernameKeyStorage();
        if (!username) {
            return '';
        }

        return `PIP_STATE_` + username;
    }

    private setPropertiesFromQueryStringOrDataAttributes() {
        const traceLoggingEnabledQuery = this.getFromQueryStringOrDataAttribute('traceLoggingEnabled');
        if (traceLoggingEnabledQuery) {
            this.base.handleTraceLogging(traceLoggingEnabledQuery == 'true');
        } else {
            this.base.handleTraceLogging();
        }

        const symbol = this.getFromQueryStringOrDataAttribute('symbol');
        if (symbol) {
            this.symbol = symbol.toUpperCase();
        } else {
            this.symbol = 'SPY';
        }

        const waitForLoginQuery = this.getFromQueryStringOrDataAttribute('waitforlogin');
        if (waitForLoginQuery) {
            this.waitForLogin = waitForLoginQuery.toLowerCase() === 'true';
        }

        const mainChartZoomLevelQuery = this.getFromQueryStringOrDataAttribute('mainchartzoomlevel');
        const mainChartZoomLevel = parseFloat(mainChartZoomLevelQuery);
        if (!isNaN(mainChartZoomLevel)) {
            this.mainChartZoomLevel = mainChartZoomLevel;
        }

        const pipChartZoomLevelQuery = this.getFromQueryStringOrDataAttribute('pipchartzoomlevel');
        const pipChartZoomLevel = parseFloat(pipChartZoomLevelQuery);
        if (!isNaN(pipChartZoomLevel)) {
            this.pipChartZoomLevel = pipChartZoomLevel;
        }

        const onReconnectStartRefreshQuery = this.getFromQueryStringOrDataAttribute('onreconnect-startrefresh');
        if (onReconnectStartRefreshQuery) {
            this.onReconnectStartRefresh = onReconnectStartRefreshQuery.toLowerCase() === 'true';
        }

        const timeframeLocalStorage = this.getTimeframe();
        const timeframeQuery = this.getFromQueryStringOrDataAttribute('timeframe');
        if (timeframeLocalStorage >= 0) {
            this.timeframe = timeframeLocalStorage;
        } else if (timeframeQuery) {
            const timeframe = TimeFrameToChartTimeFrameMapper.map(timeframeQuery);
            if (timeframe >= 0) {
                this.timeframe = timeframe;
            }
        }

        const useLoginPopupQuery = this.getFromQueryStringOrDataAttribute('useloginpopup');
        if (useLoginPopupQuery) {
            this.setUseLoginPopup(useLoginPopupQuery.toLowerCase() === 'true');
        }

        const traceQuery = this.getFromQueryStringOrDataAttribute('trace');
        if (traceQuery) {
            this.setDebugTrace(traceQuery.toLowerCase() === 'true');
        }

        const symbolLabelQuery = this.getFromQueryStringOrDataAttribute('symbolLabel');
        if (symbolLabelQuery) {
            this.symbolLabel = symbolLabelQuery;
        }

        const pnpDisabledQuery = this.getFromQueryStringOrDataAttribute('disablepip');
        if (pnpDisabledQuery && pnpDisabledQuery.toLowerCase() === 'true') {
            this.pnpEnabled = false;
        }

        const statuslineEnableQuery = this.getFromQueryStringOrDataAttribute('enableStatusLine');
        if (statuslineEnableQuery && statuslineEnableQuery.toLowerCase() === 'true') {
            this.baseElement.findFirst('.tooltip')?.removeClass('hidden');
        }

        const scrollingEnabledQuery = this.getFromQueryStringOrDataAttribute('scrollingenabled');
        if (scrollingEnabledQuery && scrollingEnabledQuery.toLowerCase() === 'true') {
            this.base.scrollingEnabled = true;
            this.base.scrollMode = ScrollMode.MouseWheel;
        }

        const symbolLookupQuery = this.getFromQueryStringOrDataAttribute('symbollookup');
        if (symbolLookupQuery) {
            this.symbollookup = symbolLookupQuery;
        }

        const symbolLookupFocusOnLoadQuery = this.getFromQueryStringOrDataAttribute('symbollookup-focusonload');
        if (symbolLookupFocusOnLoadQuery) {
            this.symbolLookupFocusOnLoadQuery = symbolLookupFocusOnLoadQuery.toLowerCase() === 'true';
        }

        const sswNoBackgroundQuery = this.getFromQueryStringOrDataAttribute('ssw-nobg');
        if (sswNoBackgroundQuery && sswNoBackgroundQuery.toLowerCase() === 'true') {
            this.sswNoBackground = true;
        }

        const sswBackgroundPercentQuery = this.getFromQueryStringOrDataAttribute('ssw-bgpercent');
        const sswBackgroundPercent = parseFloat(sswBackgroundPercentQuery);
        if (!isNaN(sswBackgroundPercent)) {
            this.sswBackgroundPercent = sswBackgroundPercent;
        }

        const sswSeparatorDarkColorQuery = this.getFromQueryStringOrDataAttribute('ssw-separatordarkcolor');
        const sswSeparatorDarkColor = this.getCorrectColorFromString(sswSeparatorDarkColorQuery);
        if (sswSeparatorDarkColor) {
            this.sswSeparatorDarkColor = sswSeparatorDarkColor;
        }

        const sswSeparatorLightColorQuery = this.getFromQueryStringOrDataAttribute('ssw-separatorlightcolor');
        const sswSeparatorLightColor = this.getCorrectColorFromString(sswSeparatorLightColorQuery);
        if (sswSeparatorLightColor) {
            this.sswSeparatorLightColor = sswSeparatorLightColor;
        }

        const cfcUpColorLightQuery = this.getFromQueryStringOrDataAttribute('changeFromCloseUpColorLight');
        const cfcUpColorLight = this.getCorrectColorFromString(cfcUpColorLightQuery);
        if (cfcUpColorLight) {
            this.cfcUpColorLight = cfcUpColorLight;
        }

        const cfcDownColorLightQuery = this.getFromQueryStringOrDataAttribute('changeFromCloseDownColorLight');
        const cfcDownColorLight = this.getCorrectColorFromString(cfcDownColorLightQuery);
        if (cfcDownColorLight) {
            this.cfcDownColorLight = cfcDownColorLight;
        }

        const cfcUpColorDarkQuery = this.getFromQueryStringOrDataAttribute('changeFromCloseUpColorDark');
        const cfcUpColorDark = this.getCorrectColorFromString(cfcUpColorDarkQuery);
        if (cfcUpColorDark) {
            this.cfcUpColorDark = cfcUpColorDark;
        }

        const cfcDownColorDarkQuery = this.getFromQueryStringOrDataAttribute('changeFromCloseDownColorDark');
        const cfcDownColorDark = this.getCorrectColorFromString(cfcDownColorDarkQuery);
        if (cfcDownColorDark) {
            this.cfcDownColorDark = cfcDownColorDark;
        }

        const previewChartLightThemeUpColorQuery = this.getFromQueryStringOrDataAttribute('previewChartLightThemeUpColor');
        const previewChartLightThemeUpColor = this.getCorrectColorFromString(previewChartLightThemeUpColorQuery);
        if (previewChartLightThemeUpColor) {
            this.previewChartLightThemeUpColor = previewChartLightThemeUpColor;
        }

        const previewChartLightThemeDownColorQuery = this.getFromQueryStringOrDataAttribute('previewChartLightThemeDownColor');
        const previewChartLightThemeDownColor = this.getCorrectColorFromString(previewChartLightThemeDownColorQuery);
        if (previewChartLightThemeDownColor) {
            this.previewChartLightThemeDownColor = previewChartLightThemeDownColor;
        }

        const lastPriceUpColorLightQuery = this.getFromQueryStringOrDataAttribute('lastPriceUpColorLight');
        const lastPriceUpColorLight = this.getCorrectColorFromString(lastPriceUpColorLightQuery);
        if (lastPriceUpColorLight) {
            this.lastPriceUpColorLight = lastPriceUpColorLight;
        }

        const lastPriceDownColorLightQuery = this.getFromQueryStringOrDataAttribute('lastPriceDownColorLight');
        const lastPriceDownColorLight = this.getCorrectColorFromString(lastPriceDownColorLightQuery);
        if (lastPriceDownColorLight) {
            this.lastPriceDownColorLight = lastPriceDownColorLight;
        }

        const lastPriceUpColorDarkQuery = this.getFromQueryStringOrDataAttribute('lastPriceUpColorDark');
        const lastPriceUpColorDark = this.getCorrectColorFromString(lastPriceUpColorDarkQuery);
        if (lastPriceUpColorDark) {
            this.lastPriceUpColorDark = lastPriceUpColorDark;
        }

        const lastPriceDownColorDarkQuery = this.getFromQueryStringOrDataAttribute('lastPriceDownColorDark');
        const lastPriceDownColorDark = this.getCorrectColorFromString(lastPriceDownColorDarkQuery);
        if (lastPriceDownColorDark) {
            this.lastPriceDownColorDark = lastPriceDownColorDark;
        }

        const rightEdgePaddingBarsDefaultQuery = this.getFromQueryStringOrDataAttribute('right-edge-padding-bar-default');
        const rightEdgePaddingBarsDefault = parseInt(rightEdgePaddingBarsDefaultQuery, 10);
        if (rightEdgePaddingBarsDefault > 0) {
            this.rightEdgePaddingBarsDefault = rightEdgePaddingBarsDefault;
        }

        // const scrollModeQuery = this.getFromQueryStringOrDataAttribute('scrollmode');
        // if (scrollModeQuery) {
        //     let scrollMode = ScrollMode.MouseWheel;
        //     if (scrollModeQuery.toLowerCase() === 'clickanddrag') {
        //         scrollMode = ScrollMode.ClickAndDrag;
        //     }

        //     this.chartContainer.scrollMode = scrollMode;
        // }

        if (this.isPnP) {
            if (timeframeLocalStorage < 0) {
                this.timeframe = Timeframe.Day;
            }

            this.chartType = ChartType.Area;
            this.displayVolumeIndicator = false;
        } else {
            const displayVolumeIndicatorQuery = this.getFromQueryStringOrDataAttribute('displayvolumeindicator');
            if (displayVolumeIndicatorQuery) {
                this.displayVolumeIndicator = displayVolumeIndicatorQuery.toLowerCase() === 'true';
            }

            const emaCloudSectionCountQuery = this.getFromQueryStringOrDataAttribute('ema-cloud-section-count');
            const emaCloudSectionCount = parseInt(emaCloudSectionCountQuery, 10);
            if (emaCloudSectionCount > 0) {
                this.emaCloudSectionCount = emaCloudSectionCount;
            }

            const showPreMarketQuery = this.getFromQueryStringOrDataAttribute('showpremarket');
            if (showPreMarketQuery) {
                this.showPreMarket = showPreMarketQuery.toLowerCase() === 'true';
            }

            const showPostMarketQuery = this.getFromQueryStringOrDataAttribute('showpostmarket');
            if (showPostMarketQuery) {
                this.showPostMarket = showPostMarketQuery.toLowerCase() === 'true';
            }

            const previewSymbolSizePercentQuery = this.getFromQueryStringOrDataAttribute('previewsymbolsizepercent');
            if (previewSymbolSizePercentQuery) {
                this.setChartPreviewSymbolSizePercent(parseInt(previewSymbolSizePercentQuery, 10));
            }
        }

        const chartTimeframeStyleQuery = this.getFromQueryStringOrDataAttribute('timeframestyle');
        if (chartTimeframeStyleQuery) {
            if (!this.isPnP) {
                if (chartTimeframeStyleQuery == 'daytrading') {
                    this.timeframe = Timeframe.FifteenMinute;
                } else if (chartTimeframeStyleQuery == 'swingtrading') {
                    this.timeframe = Timeframe.Day;
                } else if (chartTimeframeStyleQuery == 'investing') {
                    this.timeframe = Timeframe.Week;
                }
            } else {
                if (chartTimeframeStyleQuery == 'daytrading') {
                    this.timeframe = Timeframe.Day;
                } else if (chartTimeframeStyleQuery == 'swingtrading') {
                    this.timeframe = Timeframe.Week;
                } else if (chartTimeframeStyleQuery == 'investing') {
                    this.timeframe = Timeframe.Month;
                }
            }
        }

        const mainChartTimeframeQuery = this.getFromQueryStringOrDataAttribute('maintimeframe');
        if (mainChartTimeframeQuery && !this.isPnP) {
            const timeframe = TimeFrameToChartTimeFrameMapper.map(mainChartTimeframeQuery);
            if (timeframe >= 0) {
                this.timeframe = timeframe;
            }
        }

        const pipChartTimeframeQuery = this.getFromQueryStringOrDataAttribute('piptimeframe');
        if (pipChartTimeframeQuery && !this.isPnP) {
            const timeframe = TimeFrameToChartTimeFrameMapper.map(pipChartTimeframeQuery);
            if (timeframe >= 0) {
                this.timeframe = timeframe;
            }
        }

        const chartThemeQuery = this.getFromQueryStringOrDataAttribute('theme');
        if (chartThemeQuery) {
            this.theme = WebChartThemeHelpers.getTheme(chartThemeQuery.toLowerCase());
        }

        const chartModeQuery = this.getFromQueryStringOrDataAttribute('mode');
        if (chartModeQuery) {
            let chartMode = ChartMode.Standard;
            if (chartModeQuery.toLowerCase() === 'preview') {
                chartMode = ChartMode.Preview;
                this.showPreMarket = false;
                this.showPostMarket = false;
            } else if (chartModeQuery.toLowerCase() === 'popupchart') {
                chartMode = ChartMode.PopupChart;
                if (!mainChartTimeframeQuery) {
                    this.timeframe = Timeframe.FifteenMinute;
                }

                // if (!mainChartZoomLevel) {
                //     this.mainChartZoomLevel = -4;
                // }
                this.displayVolumeIndicator = false;
            }

            TraceLoggingHelpers.log('chart mode ', chartMode)
            this.chartMode = chartMode;
            this.chartStyleModel = this.getChartStyleSettings(this.theme);
            this.setChartMode(chartMode);
        } else {
            this.chartStyleModel = this.getChartStyleSettings(this.theme);
        }

        const chartUpColorQuery = this.getFromQueryStringOrDataAttribute('upcolor');
        if (chartUpColorQuery) {
            this.setSelectedColor(chartUpColorQuery, true);
        }

        const chartDownColorQuery = this.getFromQueryStringOrDataAttribute('downcolor');
        if (chartDownColorQuery) {
            this.setSelectedColor(chartDownColorQuery, true);
        }

        const backgroundTransparentQuery = this.getFromQueryStringOrDataAttribute('backgroundtrasparent');
        if (backgroundTransparentQuery) {
            this.previewChartBackgroundTrasparentMode = backgroundTransparentQuery.toLowerCase() === 'true';
        }

        const previewChartLabelPositionQuery = this.getFromQueryStringOrDataAttribute('labelposition');
        if (previewChartLabelPositionQuery) {
            this.previewChartLabelPosition = PreviewModeLabelPosition.InsideTopLeft;
            if (previewChartLabelPositionQuery.toLowerCase() === 'outsideleft') {
                this.previewChartLabelPosition = PreviewModeLabelPosition.OutsideLeft;
            } else if (previewChartLabelPositionQuery.toLowerCase() === 'outsideright') {
                this.previewChartLabelPosition = PreviewModeLabelPosition.OutsideRight;
            }
        }

        const chartModeStyleQuery = this.getFromQueryStringOrDataAttribute('modestyle');
        if (chartModeStyleQuery) {
            this.chartModeStyle = ChartModeStyle.Normal;
            if (chartModeStyleQuery.toLowerCase() === 'leftandrightlabels') {
                this.chartModeStyle = ChartModeStyle.LeftAndRightLabels;
            }
        }

        const mainChartLineAndFillColorQuery = this.getFromQueryStringOrDataAttribute('mainchartlineandfillcolor');
        if (mainChartLineAndFillColorQuery) {
            const colorSplits = mainChartLineAndFillColorQuery.split(',');
            const lineHexColor = colorSplits.length > 0 ? this.getCorrectColorFromString(colorSplits[0]) : '';
            const areaFillHexColor = colorSplits.length > 1 ? this.getCorrectColorFromString(colorSplits[1]) : '';
            const gradientHexColor = colorSplits.length > 2 ? this.getCorrectColorFromString(colorSplits[2]) : '';
            this.setMainChartLineAndFillColor(lineHexColor, areaFillHexColor, gradientHexColor);
        }

        const zoomEnabledQuery = this.getFromQueryStringOrDataAttribute('zoomenabled');
        if (zoomEnabledQuery) {
            this.zoomEnabled = zoomEnabledQuery.toLowerCase() === 'true'
        }

        const mobileZoomEnabledQuery = this.getFromQueryStringOrDataAttribute('mobile-zoom-enabled');
        if (mobileZoomEnabledQuery) {
            this.mobileZoomEnabled = mobileZoomEnabledQuery.toLowerCase() === 'true'
        }

        const marketingChartTimeframeAutoExpandQuery = this.getFromQueryStringOrDataAttribute('marketingchart-timeframeautoexpand');
        if (marketingChartTimeframeAutoExpandQuery) {
            this.marketingChartTimeframeAutoExpand = marketingChartTimeframeAutoExpandQuery.toLowerCase() === 'true'
        }

        const exitOrderDisplayBluePlotlineQuery = this.getFromQueryStringOrDataAttribute('exitorder-displayblueline');
        if (exitOrderDisplayBluePlotlineQuery) {
            this.exitOrderDisplayBluePlotline = exitOrderDisplayBluePlotlineQuery.toLowerCase() === 'true'
        }

        const futureScrollingScrollStopEnabledQuery = this.getFromQueryStringOrDataAttribute('futurescrolling-scrollstopenabled');
        if (futureScrollingScrollStopEnabledQuery) {
            this.futureScrollingScrollStopEnabled = futureScrollingScrollStopEnabledQuery.toLowerCase() === 'true'
        }

        const futureScrollingEnabledQuery = this.getFromQueryStringOrDataAttribute('futurescrollingenabled');
        if (futureScrollingEnabledQuery) {
            this.futureScrollingEnabled = futureScrollingEnabledQuery.toLowerCase() === 'true'
        }

        const futureScrollingMinDisplayBarCountQuery = this.getFromQueryStringOrDataAttribute('futureScrollingMinDisplayBarCount');
        const futureScrollingMinDisplayBarCount = parseInt(futureScrollingMinDisplayBarCountQuery);
        if (!isNaN(futureScrollingMinDisplayBarCount)) {
            this.futureScrollingMinDisplayBarCount = futureScrollingMinDisplayBarCount;
        }

        const futureScrollingBarCountQuery = this.getFromQueryStringOrDataAttribute('right-edge-padding-bar-max');
        const futureScrollingBarCount = parseFloat(futureScrollingBarCountQuery);
        if (!isNaN(futureScrollingBarCount)) {
            this.futureScrollingBarCount = futureScrollingBarCount;
        } else {
            if (!this.base.isTouchDevice) {
                this.zoomEnabled = true;
            }
        }

        const saveRightAddPaddingQuery = this.getFromQueryStringOrDataAttribute('saverightaddpadding');
        if (saveRightAddPaddingQuery) {
            this.saveRightAddPadding = saveRightAddPaddingQuery.toLowerCase() === 'true'
        }

        const pipChangePercentEnabledQuery = this.getFromQueryStringOrDataAttribute('pip-changepercent-enabled');
        if (pipChangePercentEnabledQuery) {
            this.pipChangePercentEnabled = pipChangePercentEnabledQuery.toLowerCase() === 'true'
        }

        const crosshairEnabledQuery = this.getFromQueryStringOrDataAttribute('crosshair-enabled');
        if (crosshairEnabledQuery) {
            this.crosshairEnabled = crosshairEnabledQuery.toLowerCase() === 'true'
        }

        const newTradeUIEnabledQuery = this.getFromQueryStringOrDataAttribute('newtradeui-enabled');
        if (newTradeUIEnabledQuery) {
            this.newTradeUIEnabled = newTradeUIEnabledQuery.toLowerCase() === 'true'
        }

        const emaCloudEnabledQuery = this.getFromQueryStringOrDataAttribute('emacloud-enabled');
        if (emaCloudEnabledQuery) {
            this.emaCloudEnabled = emaCloudEnabledQuery.toLowerCase() === 'true'
        }

        const zoomAndScrollEnabledQuery = this.getFromQueryStringOrDataAttribute('zoomandscroll-enabled');
        if (zoomAndScrollEnabledQuery) {
            this.zoomAndScrollEnabled = zoomAndScrollEnabledQuery.toLowerCase() === 'true'
        }

        const yAxisPanningEnabledQuery = this.getFromQueryStringOrDataAttribute('yaxispanning-enabled');
        if (yAxisPanningEnabledQuery) {
            this.yAxisPanningEnabled = yAxisPanningEnabledQuery.toLowerCase() === 'true'
        }

        const zoomAndScrollAbsoluteHeightQuery = this.getFromQueryStringOrDataAttribute('zoomandscroll-absolute-height');
        const zoomAndScrollAbsoluteHeight = parseInt(zoomAndScrollAbsoluteHeightQuery);
        if (!isNaN(zoomAndScrollAbsoluteHeight)) {
            this.zoomAndScrollAbsoluteHeight = zoomAndScrollAbsoluteHeight;
        }

        const persistPositionShareQuantityQuery = this.getFromQueryStringOrDataAttribute('persit-position-share-quantity');
        if (persistPositionShareQuantityQuery) {
            this.persistPositionShareQuantity = persistPositionShareQuantityQuery.toLowerCase() === 'true'
        }

        const priceAlertAfterHoursQuery = this.getFromQueryStringOrDataAttribute('price-alert-afterhours');
        if (priceAlertAfterHoursQuery) {
            this.priceAlertAfterHours = priceAlertAfterHoursQuery.toLowerCase() === 'true'
        }

        const pipTimeframePickerTypeQuery = this.getFromQueryStringOrDataAttribute('pip-timeframe-picker-type');
        if (pipTimeframePickerTypeQuery) {
            if (pipTimeframePickerTypeQuery.toLowerCase() === 'nonintraday"') {
                this.baseElement.querySelector<HTMLElement>('.ti-chart.pnp .controls .time .timespan')?.remove();
                return;
            }

            this.baseElement.querySelector<HTMLElement>('.ti-chart.pnp')?.addClass('clock-styles');
        } else {
            this.baseElement.querySelector<HTMLElement>('.ti-chart.pnp')?.addClass('clock-styles');
        }

        if (!this.isPnP && this.chartMode == ChartMode.Standard) {
            const chartSettingsPopupModel = this.getChartSettingsPopupModel();
            this.showPreMarket = chartSettingsPopupModel.displayPremarket;
            this.showPostMarket = chartSettingsPopupModel.displayPostmarket;
        }
    }

    private getFromQueryStringOrDataAttribute(key: string): string {
        const attrKey = ('data-tichart-' + key).toLowerCase();
        if (this.baseElement && key) {
            for (const attr of Array.from(this.baseElement.attributes)) {
                if (attr.name.toLowerCase() === attrKey) {
                    return attr.value;
                }
            }
        }

        if (this.baseElement?.parentElement) {
            for (const attr of Array.from(this.baseElement.parentElement.attributes)) {
                if (attr.name.toLowerCase() === attrKey) {
                    return attr.value;
                }
            }
        }

        const queryStringValue = UrlHelpers.getQueryString(key);
        if (queryStringValue) {
            return queryStringValue;
        }

        return '';
    }

    public setDebugTrace(on: boolean) {
        this.trace = on;
        window.trace = on;
    }

    public setUseLoginPopup(on: boolean) {
        this.useLoginPopup = on;
    }

    public setSelectedColor(colorString: string, up: boolean) {
        const colorFromString = this.getCorrectColorFromString(colorString);

        if (!colorFromString) {
            return;
        }

        if (up) {
            this.chartStyleModel.selectedUpCandleColor = new ChartStyleColorModel(color.HEX2RGB(colorString));
        } else {
            this.chartStyleModel.selectedDownCandleColor = new ChartStyleColorModel(color.HEX2RGB(colorString));
        }
    }

    private getCorrectColorFromString(colorString: string): string {
        // Hex color pattern: # followed by 3, 4, 6, or 8 hexadecimal characters
        const hexColorRegex = /^#([0-9A-F]{3}|[0-9A-F]{4}|[0-9A-F]{6}|[0-9A-F]{8})$/i;

        if (!colorString || !this.chartStyleModel) {
            return '';
        }

        if (colorString[0] !== '#') {
            colorString = '#' + colorString;
        }

        // Check if the color matches the hex color pattern
        if (!hexColorRegex.test(colorString)) {
            return '';
        }

        return colorString;
    }

    public login(username: string, password: string) {
        // Default dest to null, so the WebSocket Tunnel can route to the correct default destination - 'live' for Prod and 'aws-test' for Dev
        let dest = null;
        const destinationQueryString = UrlHelpers.getQueryString('dest');
        if (destinationQueryString) {
            dest = destinationQueryString;
        }

        this.username = username;
        this.connection.subcribeToLoginEvent(() => {
            this.onLoggedIn();
        });

        if (!this.connection.isLoggedIn()) {
            this.connection.login(username, password, 3, dest, true);
        }
    }

    public onLoggedIn() {
        if (!this.connection.isLoggedIn()) {
            return;
        }

        if (this.base.pnpMode) {
            this.onSubscribeInitChartData();
        } else {
            this.connectTradeQuote();
            this.getHoliday();
            this.getChartIndicatorsTIQ();
        }
    }

    public logout() {
        this.connection.logout();
        this.clearElements();
    }

    private clearElements() {
        const container = this.base as any;
        container.element?.findFirst('.chart-container')?.addClass('hidden');
        container.element?.findFirst('.chart')?.setInnerHtml('');

        container.element?.findFirst('.ti-chart-indicators-style')?.remove();
        container.element?.findFirst('.ti-chart-style')?.remove();
        container.element?.findFirst('.ti-chart-plot-bands-style')?.remove();
        container.element?.findFirst('.ti-chart-grid-lines-style')?.remove();
        container.element?.findFirst('.ti-chart-cross-hairs-style')?.remove();

        container.pnpContainer?.element?.findFirst('.chart-container')?.addClass('hidden');
        container.pnpContainer?.element?.findFirst('.chart')?.setInnerHtml('');

        container.pnpContainer?.element?.findFirst('.ti-chart-indicators-style')?.remove();
        container.pnpContainer?.element?.findFirst('.ti-chart-style')?.remove();
        container.pnpContainer?.element?.findFirst('.ti-chart-plot-bands-style')?.remove();
        container.pnpContainer?.element?.findFirst('.ti-chart-grid-lines-style')?.remove();
        container.pnpContainer?.element?.findFirst('.ti-chart-cross-hairs-style')?.remove();

        const summaryStatusLines = this.base.getComponentsByType(MarketingWebChartSummaryStatusLineComponent) as Array<MarketingWebChartSummaryStatusLineComponent>;
        if (summaryStatusLines?.length) {
            summaryStatusLines[0].hide();
        }
    }

    private getHoliday() {
        if (!this.isPnP) {
            this.chartTraceElement?.findFirst('.holiday-request span')?.setInnerHtml(DateHelpers.getTraceLoggingTimeStamp());
        }

        const notifyHolidayWatchers = (holidayResponse: Array<HolidayModel>) => {
            if (window.holidayWatchers && window.holidayWatchers.length) {
                for (let i = 0; i < window.holidayWatchers.length; i++) {
                    window.holidayWatchers[i](holidayResponse);
                }
            }
        };

        const addHolidayWatchers = () => {
            if (!window.holidayWatchers) {
                window.holidayWatchers = [];
            }

            window.holidayWatchers.push((holidayResponse: Array<HolidayModel>) => {
                setHolidays(holidayResponse);

                TraceLoggingHelpers.log('subscribeInitChartData addHolidayWatchers()')
                if (!this.isPnP) {
                    this.chartTraceElement?.findFirst('.holiday-response span')?.setInnerHtml(DateHelpers.getTraceLoggingTimeStamp());
                }

                this.handleLoadQueue(InitQueue.HOLIDAY);
            });
        };

        const setHolidays = (holidayResponse: Array<HolidayModel>) => {
            let holidayManager = getHolidayManager();
            holidayManager.setHolidays(holidayResponse);
        };

        const initMarketSchedule = () => {
            let holidayManager = getHolidayManager();
            holidayManager.initMarketSchedule('us_equities').then(() => {
                this.handleLoadQueue(InitQueue.MARKET_SCHEDULE);
            });
        };

        if (!window.loadedHoliday) {
            window.loadedHoliday = true;
            this.holidayClient.getHoliday().then(holidayResponse => {
                if (!holidayResponse.isSuccess) {
                    return;
                }

                setHolidays(holidayResponse.data);

                TraceLoggingHelpers.log('subscribeInitChartData getHoliday()')
                notifyHolidayWatchers(holidayResponse.data);
                if (!this.isPnP) {
                    this.chartTraceElement?.findFirst('.holiday-response span')?.setInnerHtml(DateHelpers.getTraceLoggingTimeStamp());
                }

                this.handleLoadQueue(InitQueue.HOLIDAY);
            });
        } else {
            addHolidayWatchers();
        }

        initMarketSchedule();
    }

    private handleLoadQueue(doneQueue: InitQueue) {
        const ind = this.initQueues.findIndex(x => x == doneQueue);
        if (ind > -1) {
            this.initQueues.splice(ind, 1);
        }

        if (this.initQueues.length == 0) {
            this.init();
        }
    }

    private init() {
        this.setChartPropertyFromMarketSchedule();
        this.onSubscribeInitChartData();
        this.requestPriceAlerts();
        this.connectPaperTrade();
    }

    private setChartPropertyFromMarketSchedule() {
        const holidayManager = getHolidayManagerInstance();
        this.isPreMarketDisabled = holidayManager.isPreMarketDisabled();
        this.isPostMarketDisabled = holidayManager.isPostMarketDisabled();

        const marketSchedule = holidayManager.getMarketSchedule();
        this.preMarketMinutes = marketSchedule.preMarketMinutes;
        this.postMarketMinutes = marketSchedule.postMarketMinutes;

        if (this.isPreMarketDisabled) {
            this.showPreMarket = false;
        }

        if (this.isPostMarketDisabled) {
            this.showPostMarket = false;
        }
    }

    private getChartIndicatorsTIQ() {
        if (this.isPnP) return;
        const notifyChartIndicatorsTIQWatchers = (response: any) => {
            if (window.chartIndicatorsTIQWatchers && window.chartIndicatorsTIQWatchers.length) {
                for (let i = 0; i < window.chartIndicatorsTIQWatchers.length; i++) {
                    window.chartIndicatorsTIQWatchers[i](response);
                }
            }
        };

        const addChartIndicatorsTIQWatchers = () => {
            if (!window.chartIndicatorsTIQWatchers) {
                window.chartIndicatorsTIQWatchers = [];
            }

            window.chartIndicatorsTIQWatchers.push((response: any) => {
                this.onSubscribeInitChartIndicatorsTIQData(response);
            });
        };

        if (!window.loadedChartIndicatorsTIQ) {
            window.loadedChartIndicatorsTIQ = true;
            this.chartIndicatorsClient.getChartIndicators().then(response => {
                if (!response.isSuccess) {
                    return;
                }

                notifyChartIndicatorsTIQWatchers(response.data);
                this.onSubscribeInitChartIndicatorsTIQData(response.data);
            });
        } else {
            addChartIndicatorsTIQWatchers();
        }
    }

    public override subscribeChartRequestingNewSymbolData(callback: Function) {
        this.subscribeChartRequestingNewSymbolDataCallbacks.push(callback);
    }

    private onSubscribeChartRequestingNewSymbolData() {
        for (const callback of this.subscribeChartRequestingNewSymbolDataCallbacks) {
            callback(this.symbol, this.timeframe);
        }
    }

    public override subscribeInitChartData(callback: Function) {
        this.subscribeInitChartDataCallbacks.push(callback);
    }

    private initMarketingXAxisPlotlineInterval: any;
    private chartFinishedRenderInterval: any;
    public onSubscribeInitChartData() {
        if (!this.symbol || (this.isPnP && this.chartMode == ChartMode.Preview)) {
            return;
        }

        this.requestingInitData = true;
        this.requestHistoryDataCount = 0;

        const container = this.base as any;

        if (!this.isPnP && this.chartMode == ChartMode.Preview) {
            container.element?.findFirst('.chart-container')?.addClass('hidden');
            container.element?.findFirst('.chart-container')?.addClass('no-opacity');
        }

        const chartDataRequest = this.getChartDataRequest(350);
        if (this.startingRequestTime) {
            const startingGetChartDataTime = new Date();
            TraceLoggingHelpers.log(`web chart ${this.base.id} subscribeInitChartData startingGetChartDataTime time: ${((startingGetChartDataTime.valueOf() - this.startingRequestTime.valueOf()) / 1000)}s`)
        }

        if (!this.isPnP) {
            this.chartTraceElement?.findFirst('.request span')?.setInnerHtml(DateHelpers.getTraceLoggingTimeStamp());
        } else {
            this.chartTraceElement?.findFirst('.pip-request span')?.setInnerHtml(DateHelpers.getTraceLoggingTimeStamp());
        }

        const firstLoad = !this.singleStockWindowModel.symbol;
        const symbolChanged = this.symbol != this.singleStockWindowModel.symbol;
        if (symbolChanged && !this.isPnP) {
            this.switchingSymbol = true;
            this.singleStockWindowModel = new SingleStockWindowDataModel();
            this.setupSubscription_SingleStockWindowData(this.singleStockWindowModel);
            this.topListClient?.unsubscribe();
            // this.lastPrice = 0;
            // this.onSubscribeLastPriceData();
            this.unsubscribeTradeQuote();
        } else {
            this.onSubscribeDrawEarningNotificationData(null);
            this.populateEarningNotification(null);
        }

        this.saveTimeframe();

        this.onSubscribeChartRequestingNewSymbolData();

        TraceLoggingHelpers.log('chartDataRequest ', chartDataRequest)

        // const user = this.connection.getCurrentUser();
        this.temporaryPositions = [];
        this.base.drawingToolsManager?.clearAll();

        this.chartDataClient.getChartData(chartDataRequest).then((response) => {
            if (!response.isSuccess) {
                return;
            }

            if (!this.isPnP) {
                this.chartTraceElement?.findFirst('.response span')?.setInnerHtml(DateHelpers.getTraceLoggingTimeStamp());
            } else {
                this.chartTraceElement?.findFirst('.pip-response span')?.setInnerHtml(DateHelpers.getTraceLoggingTimeStamp());
            }

            this.base.isDemo = this.connection.isDemo();
            this.username = !this.base.isDemo ? this.connection.getCurrentUser()?.username ?? 'demo' : 'demo';

            this.localStorageDrawingToolsKey = 'DWTK_' + this.symbol + '_' + this.timeframe + '_' + this.getUsernameKeyStorage();
            this.localStorageDrawingToolsSettings = 'DWTS_' + this.getUsernameKeyStorage();

            let model = JSApiPointToTiChartDataPointMapper.map(response.data, 0, this.showPreMarket, this.showPostMarket);
            TraceLoggingHelpers.log('getChartData ', { response: response, model: model })
            if (model?.length) {
                const lastPoint = model[model.length - 1];
                const firstPoint = model[0];
                this.lastPoint = lastPoint;

                // this.lastPrice = lastPoint.close;

                this.oldestDate = firstPoint.start;
                this.newestDate = lastPoint.start;

                if (model.length > 2) {
                    if (!this.isPnP && this.chartMode == ChartMode.Preview) {
                        this.setPointFromYesterdayClose(model, this.lastPoint);
                        model = this.getPointsFromYesterdayClose(model);

                        TraceLoggingHelpers.log(' model getPoints ', model)
                    } else if (this.isPnP && this.chartMode == ChartMode.Standard) {
                        // this.base.changeFromClosePipChartFirstBarClose = firstPoint.close;
                        // this.base.changeFromClosePipChartFirstBarX = firstPoint.xValue;
                        // this.base.changeFromClosePipChartLastBarClose = lastPoint.close;
                        // this.base.changeFromClosePipChartLastBarX = lastPoint.xValue;
                        // this.calcCFCPip();
                    }
                }

                this.updateMarketingSummaryStatusLine(false);
            }

            if (this.startingRequestTime) {
                const finishedRequestTime = new Date();
                TraceLoggingHelpers.log(`web chart ${this.base.id} subscribeInitChartData total request time: ${((finishedRequestTime.valueOf() - this.startingRequestTime.valueOf()) / 1000)}s`)
            }

            for (const callback of this.subscribeInitChartDataCallbacks) {
                callback(model, this.timeframe, this.symbol, this.chartType);
            }

            const emaSettings = this.displayEMACloudIndicator && this.emaCloudSettings?.items?.length ? this.emaCloudSettings : null;
            const tiIndicatorsData = this.indicatorManager.mapTiIndicatorPoints(model, emaSettings)

            TraceLoggingHelpers.log('tiIndicatorsData', tiIndicatorsData)
            if (!this.isPnP) {
                container.element?.findFirst('.chart-container')?.removeClass('hidden');
            }

            // this.onSubscribeTICompanyLogoData();
            this.onSubscribeChartStyleData();
            this.onSubscribeInitChartIndicatorsData(tiIndicatorsData);
            // this.onSubscribeLastPriceData();
            this.onSubscribeInitChartDrawingToolSettings();
            this.onSubscribeInitChartDrawingTools();
            this.onSubscribeEmojiData();

            if (symbolChanged) {
                this.requestPriceAlerts();

                if (!this.isPnP) {
                    this.requestCompanyProfileData();
                    this.requestHaltData();
                    this.requestTopListData();
                    this.subscribeTradeQuote();
                }
            } else {
                setTimeout(() => {
                    this.onSubscribeDrawEarningNotificationData(this.singleStockWindowModel);
                    this.populateEarningNotification(this.singleStockWindowModel);
                }, 1000);
            }

            this.populatePnl();
            this.popuplateOrderExecutionData();
            this.popuplateTradeOrderData();

            this.onSubscribeUpdate10sRefreshChartData();
            this.set10sRefreshTimer();

            this.requestingInitData = false;
            this.switchingSymbol = false;

            setTimeout(() => {
                this.base.tiChartSeries?.afterResize();
            }, 1000);

            if (!this.isPnP && this.chartMode == ChartMode.Preview) {
                clearInterval(this.initMarketingXAxisPlotlineInterval);
                this.initMarketingXAxisPlotlineInterval = setInterval(() => {
                    const yAxis = this.base.tiChartSeries?.highStockChart?.yAxis;
                    if (yAxis?.length) {
                        clearInterval(this.initMarketingXAxisPlotlineInterval);
                        this.setPreviewModeXAxisPointPlotLine();
                        if (this.startingRequestTime) {
                            const finishedRequestTime = new Date();
                            TraceLoggingHelpers.log(`web chart ${this.base.id} subscribeInitChartData finished render time: ${((finishedRequestTime.valueOf() - this.startingRequestTime.valueOf()) / 1000)}s`)
                        }
                    }
                }, 50);

                if (this.chartModeStyle != ChartModeStyle.LeftAndRightLabels) {
                    setTimeout(() => {
                        container.element?.findFirst('.chart-container')?.removeClass('no-opacity');
                    }, 100);
                }
            }

            if (!this.isPnP) {
                const clearIntervalAndAddTimeStamp = () => {
                    clearInterval(this.chartFinishedRenderInterval);
                    this.chartTraceElement?.findFirst('.finished span')?.setInnerHtml(DateHelpers.getTraceLoggingTimeStamp());
                    setTimeout(() => {
                        this.onSubscribeUpdate10sRefreshChartData();
                        this.triggerChartReadyCustomEvent();
                        this.enableAnnotations(firstLoad ? 5000 : 3000);
                        this.enableExitOrderAnnotations(firstLoad ? 3000 : 1500);
                    }, 500);
                };

                clearInterval(this.chartFinishedRenderInterval);
                this.chartFinishedRenderInterval = setInterval(() => {
                    const container = this.base as any;
                    const yAxis = this.base.tiChartSeries?.highStockChart?.yAxis;
                    if (container.pnpContainer && this.chartMode == ChartMode.Standard) {
                        const pipYAxis = container.pnpContainer.tiChartSeries?.highStockChart?.yAxis;
                        if (yAxis?.length && pipYAxis?.length) {
                            clearIntervalAndAddTimeStamp();
                        }
                    } else if (yAxis?.length) {
                        clearIntervalAndAddTimeStamp();
                    }
                }, 10);
            }
        });
    }

    private enableAnnotationTimer: any;
    public enableAnnotations(time: number, populate?: boolean) {
        clearTimeout(this.enableAnnotationTimer);
        this.enableAnnotationTimer = setTimeout(() => {

            const exitOrders = this.base.getComponentsByType(DrawExitOrderComponent)[0] as DrawExitOrderComponent;
            exitOrders?.enable();

            const pnl = this.base.getComponentsByType(PnlComponent)[0] as PnlComponent;
            pnl?.enableAnnotations();

            const orderExecution = this.base.getComponentsByType(DrawOrderExecutionComponent)[0] as DrawOrderExecutionComponent;
            orderExecution?.enable();

            const tradeOrders = this.base.getComponentsByType(DrawTradeOrdersComponent)[0] as DrawTradeOrdersComponent;
            tradeOrders?.enable();

            // this.base.tiChartSeries.setEMACloundData();
            if (populate) {
                this.popuplateOrderExecutionData();
                this.popuplateTradeOrderData();
            }
        }, time);
    }

    private triggerChartReadyCustomEvent() {
        this.base.domEvents.triggerCustomEvent('chartIsReady', {
            detail: { chartId: this.base.id, chartIndex: this.base.index, symbol: this.symbol }
        });
    }

    private enableExitOrderAnnotationTimer: any;
    public enableExitOrderAnnotations(time: number) {
        clearTimeout(this.enableExitOrderAnnotationTimer);
        this.enableExitOrderAnnotationTimer = setTimeout(() => {
            const exitOrders = this.base.getComponentsByType(DrawExitOrderComponent)[0] as DrawExitOrderComponent;
            exitOrders?.enable();

            const pnl = this.base.getComponentsByType(PnlComponent)[0] as PnlComponent;
            pnl?.enableAnnotations();
        }, time);
    }

    public disableAnnotations() {
        const exitOrders = this.base.getComponentsByType(DrawExitOrderComponent)[0] as DrawExitOrderComponent;
        exitOrders?.disable();

        const pnl = this.base.getComponentsByType(PnlComponent)[0] as PnlComponent;
        pnl?.disableAnnotations();

        const orderExecution = this.base.getComponentsByType(DrawOrderExecutionComponent)[0] as DrawOrderExecutionComponent;
        orderExecution?.disable();

        const tradeOrders = this.base.getComponentsByType(DrawTradeOrdersComponent)[0] as DrawTradeOrdersComponent;
        tradeOrders?.disable();
    }

    private setPreviewModeXAxisPointPlotLine(setHeightOnly?: boolean) {
        if (this.chartMode == ChartMode.Preview && !this.isPnP) {
            this.xAxisPlotLine?.remove();
            this.xAxisPlotLine = null;
            const color = this.marketingSummaryColor.toHex();

            let setYAxisHeightOnly = false;
            if (setHeightOnly) {
                setYAxisHeightOnly = true;
            }

            TraceLoggingHelpers.log('setPreviewModeXAxisPointPlotLine ', this.pointFromYesterday?.close ?? 0)
            this.xAxisPlotLine = new MarketingWebChartXAxisPlotLineComponent(
                this.base,
                this.pointFromYesterday?.close ?? 0,
                color,
                setYAxisHeightOnly,
                this.isMarketingSummaryUp);
        }
    }

    private xAxisPlotLine: MarketingWebChartXAxisPlotLineComponent | null = null;
    private xAxisPlotLinePoint: TIChartPointModel | null = null;
    private highestHighPoint: TIChartPointModel | null = null;
    public getPointsFromYesterdayClose(model: Array<TIChartPointModel>): Array<TIChartPointModel> {
        if (this.pointFromYesterday && this.chartMode == ChartMode.Preview) {
            let newXValue = 0;
            let newPointsToAdd: Array<TIChartPointModel> = [];
            this.xAxisPlotLinePoint = null;

            for (const point of model) {
                if (point.xValue > this.pointFromYesterday.xValue) {
                    point.xValue = newXValue;
                    newPointsToAdd.push(point);

                    newXValue++;

                    if (!this.xAxisPlotLinePoint) {
                        this.xAxisPlotLinePoint = point;
                    }
                }

                if (!this.highestHighPoint || this.highestHighPoint.high < point.high) {
                    this.highestHighPoint = point;
                }
            }

            TraceLoggingHelpers.log('this.xAxisPlotLinePoint ', this.xAxisPlotLinePoint)
            model = newPointsToAdd;
        }

        return model;
    }

    private setPointFromYesterdayClose(points: Array<TIChartPointModel>, lastPoint: TIChartPointModel) {
        const lastPointInMarketTimeZone = DateHelpers.toMarketTimeZone(lastPoint.start);
        const lastPointInMarketTimeZoneWithoutTime = lastPointInMarketTimeZone.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
        let pointsWithoutPreAndPostMarkets = points.filter((x: TIChartPointModel) => {
            const startTime = DateHelpers.toMarketTimeZone(x.start);
            return DateHelpers.isMarketOpen(startTime.toJSDate()) && startTime < lastPointInMarketTimeZoneWithoutTime;
        });

        pointsWithoutPreAndPostMarkets = pointsWithoutPreAndPostMarkets.sort((a, b) => a.startTimeT - b.startTimeT);

        this.pointFromYesterday = pointsWithoutPreAndPostMarkets.length ? pointsWithoutPreAndPostMarkets[pointsWithoutPreAndPostMarkets.length - 1] : null;
        // TraceLoggingHelpers.log('chart id ', this.baseElement.id, 'this.pointFromYesterday ', this.pointFromYesterday, 'lastPoint ', lastPoint)
    }

    private timer10sRefresh: any;
    private set10sRefreshTimer() {
        clearTimeout(this.timer10sRefresh);
        this.timer10sRefresh = setTimeout(() => {
            this.onSubscribeUpdate10sRefreshChartData();
        }, 10000)
    }

    private onReconnected() {
        if (this.onReconnectStartRefresh) {
            TraceLoggingHelpers.log(`web chart ${this.base.id} will start refresh when reconnect`);
        }

        this.connection.subscribeOnReconnected(() => {
            TraceLoggingHelpers.log(DateHelpers.getTraceLoggingTimeStamp(), ' Chart Reconnected ...');
            if (this.onReconnectStartRefresh) {
                this.set10sRefreshTimer();
            }
        });
    }

    private connectPaperTrade() {
        if (this.isPnP || this.chartMode != ChartMode.Standard) {
            return;
        }

        this.papertTradeConnected = true;
        this.brokerManagerClient.Connect("Paper", (messageType, data1, data2) => {
            TraceLoggingHelpers.log('PAPAER TRADE' + (new Date()).toISOString() + " : " + messageType, { 'data1': data1, 'data2': data2 })
            switch (messageType) {
                case "broker-accounts":
                    // case "broker-account-add":
                    // case "broker-account-update":

                    this.setupSubscription_TradeAccountsData(data1);
                    break;

                case "broker-positions":
                case "broker-position-add":
                case "broker-positions-update":

                    this.setupSubscription_TradePositionsData(data1);
                    //   brokerPositions.value = generateTable(data1);
                    break;


                case "broker-orders":
                case "broker-order-add":
                case "broker-order-update":

                    this.setupSubscription_TradeOrderData(data1);

                    break;

                case "broker-executions":
                case "broker-executions-add":

                    this.setupSubscription_OrderExecutionData(data1);

                    break;

                default:

                    break;
            }
        });
    }

    private alerts: Array<LimitAlertModel> = [];
    private requestPriceAlerts() {
        if (this.isPnP || this.chartMode != ChartMode.Standard) {
            return;
        }

        // setupSubscription_PriceAlertsData
        TraceLoggingHelpers.log('requestPriceAlerts');
        this.priceAlertClient.subscribe((response: ClientResponseModel<Array<PriceAlertResponseModel>>) => {
            if (!response.isSuccess || !response.data?.length) {
                return;
            }

            const existingTriggeredPriceAlertsKey = this.getPriceAlertTriggeredKeyStorage();
            const existingTriggeredPriceAlertsRaw = localStorage.getItem(existingTriggeredPriceAlertsKey);
            const newTriggeredPriceAlerts = [];

            let existingTriggeredPriceAlerts = existingTriggeredPriceAlertsRaw ? JSON.parse(existingTriggeredPriceAlertsRaw) : [];
            for (const alert of response.data) {
                if (alert.status === 'TRIGGERED' && alert.triggered
                    && alert.triggered.valueOf() > this.startingRequestTime.valueOf()
                    && !existingTriggeredPriceAlerts.some((x => x == alert.id))) {
                    newTriggeredPriceAlerts.push(alert.id);
                }
            }

            if (newTriggeredPriceAlerts.length) {
                existingTriggeredPriceAlerts = existingTriggeredPriceAlerts.concat(newTriggeredPriceAlerts);
                localStorage.setItem(existingTriggeredPriceAlertsKey, JSON.stringify(existingTriggeredPriceAlerts));
                for (const newTriggeredPriceAlert of newTriggeredPriceAlerts) {
                    const alert = response.data.find(x => x.id == newTriggeredPriceAlert);
                    let message = `<p>${alert?.tooltip ?? ''}</p>`;
                    if (alert.symbol.toLowerCase() != this.symbol.toLowerCase()) {
                        const setSymbol = `window.chartsById['${this.base.id}'].setSymbol('${alert.symbol.toUpperCase()}')`;
                        message += `<p>Change to Triggered symbol, <a href="#" onclick="${setSymbol};return false;">${alert.symbol.toUpperCase()}</a></p>`
                    }
                    this.infoPopup?.show('Price Alert', message);
                }
            }

            this.alerts = JSApiPriceAlertToLimitAlertMapper.map(response, this.symbol);
            TraceLoggingHelpers.log('requestPriceAlerts response', ObjectHelpers.copy(this.alerts));
            this.setupSubscription_PriceAlertsData(this.alerts);
        });
    }

    private requestHaltData() {
        if (!this.symbol) {
            return;
        }

        const haltedRequest = new HaltedRequestModel();
        haltedRequest.symbol = this.symbol;
        this.haltedClient.subscribe(haltedRequest, (response: ClientResponseModel<HaltedResponseModel>) => {
            if (this.subscribeHaltedNotificationDataCallbacks?.length) {
                for (const subscribe_SingleStockWindowData of this.subscribeHaltedNotificationDataCallbacks) {
                    subscribe_SingleStockWindowData(response.data?.showNotification ?? false, response.data?.reason ?? '');
                }
            }
        });
    }

    private connectTradeQuote() {
        this.throttledMarketDataClient.Connect()?.then(() => {
            this.subscribeTradeQuote();
        });
    }

    private tradeQuotePercentChange: number | null = null;
    private tradeQuoteDollarChange: number | null = null;
    private tradeQuoteVolume: number | null = null;
    private tradeQuoteTradeTime: Date | null = null;
    private subscribeTradeQuote() {
        if (!this.symbol) {
            return;
        }

        TraceLoggingHelpers.log(`SubscribeTradeQuote start id: ${this.base.id}, symbol: ${this.symbol}`);
        this.throttledMarketDataClient.SubscribeTradeQuote(this.symbol, this.base.id, true, (response) => {
            this.lastPrice = response.TradePrice;
            this.tradeQuotePercentChange = response.PercentChange;
            this.tradeQuoteDollarChange = response.DollarChange;
            this.tradeQuoteVolume = response.Volume;
            this.tradeQuoteTradeTime = response.TradeTime;;

            TraceLoggingHelpers.log('SubscribeTradeQuote response', response);
            TraceLoggingHelpers.log('SubscribeTradeQuote lastPrice', this.lastPrice);
            this.setupSubscription_SingleStockWindowData(this.singleStockWindowModel);
            this.onSubscribeLastPriceData();
        });
    }

    private unsubscribeTradeQuote() {
        if (!this.symbol) {
            return;
        }

        this.tradeQuotePercentChange = null;
        this.tradeQuoteDollarChange = null;
        this.tradeQuoteVolume = null;
        this.throttledMarketDataClient.UnSubscribeTradeQuote(this.symbol, this.base.id);
    }

    private requestTopListData() {
        if (!this.symbol) {
            return;
        }

        const topListRequest = new TopListDataRequestModel();
        topListRequest.resultCount = 1;

        topListRequest.singleSymbol = this.symbol;
        topListRequest.streaming = true;

        if (this.chartMode == ChartMode.Preview) {
            topListRequest.outsideMarketHours = true;
        }

        topListRequest.skipMetaData = false;
        topListRequest.addExtraColumn('D_Name');
        topListRequest.addExtraColumn('Price');
        topListRequest.addExtraColumn('EarningD');
        topListRequest.addExtraColumn('FCD');
        topListRequest.addExtraColumn('FCP');
        topListRequest.addExtraColumn('last_price');
        topListRequest.addExtraColumn('RV');
        topListRequest.addExtraColumn('TV');
        topListRequest.addExtraColumn('Float');
        topListRequest.addExtraColumn('SFloat');
        topListRequest.addExtraColumn('TRangeD');
        topListRequest.addExtraColumn('DTC');
        topListRequest.addExtraColumn('PV');
        topListRequest.addExtraColumn('EPS');
        topListRequest.addExtraColumn('MCap');
        topListRequest.addExtraColumn('Revenue');

        topListRequest.addExtraColumn('ShOut');
        topListRequest.addExtraColumn('Cash');
        topListRequest.addExtraColumn('PCPV');
        topListRequest.addExtraColumn('UpJan1P');
        topListRequest.addExtraColumn('DV');
        topListRequest.addExtraColumn('Debt');
        topListRequest.addExtraColumn('RD');
        topListRequest.addExtraColumn('R5D');
        topListRequest.addExtraColumn('RY');

        topListRequest.addExtraColumn('PCAV');
        topListRequest.addExtraColumn('CTV');
        topListRequest.addExtraColumn('PTV');
        topListRequest.addExtraColumn('U20DD');
        topListRequest.addExtraColumn('U20DP');
        topListRequest.addExtraColumn('U5DD');
        topListRequest.addExtraColumn('U5DP');
        topListRequest.addExtraColumn('QEarnG');
        topListRequest.addExtraColumn('SMA200');
        topListRequest.addExtraColumn('DRSI');
        topListRequest.addExtraColumn('Vol1');
        topListRequest.addExtraColumn('VWV');

        TraceLoggingHelpers.log('chart topListClient.subscribe start', topListRequest);
        this.topListClient.subscribe(topListRequest, (response: ClientResponseModel<TopListDataResponseModel>) => {
            if (!response.isSuccess || !response.data || this.switchingSymbol) {
                return;
            }

            const data = response.data;
            TraceLoggingHelpers.log('chart topListClient.subscribe ', response);

            const singleStockWindowModel = new SingleStockWindowDataModel();
            singleStockWindowModel.start = response.data.start;
            singleStockWindowModel.end = response.data.end;
            singleStockWindowModel.symbol = topListRequest.singleSymbol;

            for (let i = 0; i < data.rows.length; i++) {
                const row = data.rows[i];
                const earningsDateUnixtime = parseFloat(this.getRowValue(row, '[EarningD]'));

                this.companyName = this.getRowValue(row, '[D_Name]')

                singleStockWindowModel.earningsDateUnixtime = earningsDateUnixtime;
                singleStockWindowModel.relativeVolume = parseFloat(this.getRowValue(row, '[RV]'));
                singleStockWindowModel.totalVolume = parseFloat(this.getRowValue(row, '[TV]'));
                singleStockWindowModel.float = parseFloat(this.getRowValue(row, '[Float]'));
                singleStockWindowModel.shortFloat = parseFloat(this.getRowValue(row, '[SFloat]'));
                singleStockWindowModel.todayRange = parseFloat(this.getRowValue(row, '[TRangeD]'));
                singleStockWindowModel.daysToCover = parseFloat(this.getRowValue(row, '[DTC]'));
                singleStockWindowModel.volumeToday = parseFloat(this.getRowValue(row, '[PV]'));
                singleStockWindowModel.changesFromCloseDollar = parseFloat(this.getRowValue(row, '[FCD]'));
                singleStockWindowModel.changesFromClosePercentage = parseFloat(this.getRowValue(row, '[FCP]'));
                singleStockWindowModel.earningsPerShare = parseFloat(this.getRowValue(row, '[EPS]'));
                singleStockWindowModel.marketCap = parseFloat(this.getRowValue(row, '[MCap]'));
                singleStockWindowModel.revenue = parseFloat(this.getRowValue(row, '[Revenue]'));

                singleStockWindowModel.sharesOutstanding = parseFloat(this.getRowValue(row, '[ShOut]'));
                singleStockWindowModel.cash = parseFloat(this.getRowValue(row, '[Cash]'));
                singleStockWindowModel.optionsVolumeToday = parseFloat(this.getRowValue(row, '[PCPV]'));
                singleStockWindowModel.changeSinceJanuary = parseFloat(this.getRowValue(row, '[UpJan1P]'));
                singleStockWindowModel.dollarVolume = parseFloat(this.getRowValue(row, '[DV]'));
                singleStockWindowModel.currentDebt = parseFloat(this.getRowValue(row, '[Debt]'));
                singleStockWindowModel.positionInRange = parseFloat(this.getRowValue(row, '[RD]'));
                singleStockWindowModel.positionIn5DayRange = parseFloat(this.getRowValue(row, '[R5D]'));
                singleStockWindowModel.positionInYearRange = parseFloat(this.getRowValue(row, '[RY]'));

                singleStockWindowModel.optionsVolume = parseFloat(this.getRowValue(row, '[PCAV]'));
                singleStockWindowModel.callVolumeToday = parseFloat(this.getRowValue(row, '[CTV]'));
                singleStockWindowModel.putVolumeToday = parseFloat(this.getRowValue(row, '[PTV]'));
                singleStockWindowModel.changeLastMonth = parseFloat(this.getRowValue(row, '[U20DD]'));
                singleStockWindowModel.changeIn20Days = parseFloat(this.getRowValue(row, '[U20DP]'));
                singleStockWindowModel.changeLastWeek = parseFloat(this.getRowValue(row, '[U5DD]'));
                singleStockWindowModel.changeIn5Days = parseFloat(this.getRowValue(row, '[U5DP]'));
                singleStockWindowModel.quarterlyEarningsGrowth = parseFloat(this.getRowValue(row, '[QEarnG]'));
                singleStockWindowModel.sma200 = parseFloat(this.getRowValue(row, '[SMA200]'));
                singleStockWindowModel.dailyRSI = parseFloat(this.getRowValue(row, '[DRSI]'));
                singleStockWindowModel.volume1Minute = parseFloat(this.getRowValue(row, '[Vol1]'));
                singleStockWindowModel.volatility = parseFloat(this.getRowValue(row, '[VWV]'));

                singleStockWindowModel.companyName = this.companyName;
            }

            this.singleStockWindowModel = singleStockWindowModel;

            this.updateMarketingSummaryStatusLine(false);
            this.setupSubscription_SingleStockWindowData(singleStockWindowModel);

                setTimeout(() => {
                    this.onSubscribeDrawEarningNotificationData(singleStockWindowModel)
                    this.populateEarningNotification(singleStockWindowModel)
                }, 500);
            },
            (metaData: any) => {
                TraceLoggingHelpers.log("toplist metadata response", metaData);
            });
    }

    private marketingSummaryColor = new ChartStyleColorModel();
    private isMarketingSummaryUp = false;
    private updateMarketingSummaryStatusLine(isResize: boolean) {
        const summaryStatusLines = this.base.getComponentsByType(MarketingWebChartSummaryStatusLineComponent) as Array<MarketingWebChartSummaryStatusLineComponent>;
        if (!summaryStatusLines?.length) {
            return;
        }

        const summaryStatus = summaryStatusLines[0];
        if (this.chartMode != ChartMode.Preview) {
            summaryStatus.hide();
            return;
        }

        const changesManager = this.base.getComponentsByType(SingleStockWindowChangeFromCloseManager)?.[0] as SingleStockWindowChangeFromCloseManager;
        if (!this.lastPoint || !summaryStatusLines?.length
            || SingleStockWindowDataModel.doesNotHaveValue(this.singleStockWindowModel?.changesFromCloseDollar)
            || SingleStockWindowDataModel.doesNotHaveValue(this.singleStockWindowModel?.changesFromClosePercentage)
            || !changesManager) {
            return;
        }

        this.isMarketingSummaryUp = false;

        changesManager.updateSingleStockData(this.singleStockWindowModel);
        changesManager.getChangeFromCloseValues().then((changesValue) => {
            if (!changesValue) {
                summaryStatus.hide();
                return;
            }
            
            const render = () => {
                summaryStatus.setValue(this.lastPrice, changesValue.changesFromClosePercentage, changesValue.changesFromCloseDollar);
                // TraceLoggingHelpers.log('changesFromClose ', changesFromClose)
                if (parseFloat(changesValue.changesFromCloseDollar.toFixed(2)) >= 0) {
                    this.isMarketingSummaryUp = true;

                    if (!this.chartStyleModel.isDarkTheme && this.previewChartLightThemeUpColor) {
                        this.marketingSummaryColor = new ChartStyleColorModel(color.HEX2RGB(this.previewChartLightThemeUpColor));
                    } else {
                        this.marketingSummaryColor = this.chartStyleModel.selectedUpCandleColor;
                    }

                    const colorHex = this.marketingSummaryColor.toHex();
                    this.setMainChartLineAndFillColor(colorHex, colorHex, colorHex);
                } else {

                    if (!this.chartStyleModel.isDarkTheme && this.previewChartLightThemeDownColor) {
                        this.marketingSummaryColor = new ChartStyleColorModel(color.HEX2RGB(this.previewChartLightThemeDownColor));
                    } else {
                        this.marketingSummaryColor = this.chartStyleModel.selectedDownCandleColor;
                    }

                    const colorHex = this.marketingSummaryColor.toHex();
                    this.setMainChartLineAndFillColor(colorHex, colorHex, colorHex);
                }

                this.onSubscribeChartStyleData();

                this.renderDebugLabel();
                summaryStatus.render(this.marketingSummaryColor, this.isMarketingSummaryUp, this.getSymbolLabel(), this.chartStyleModel.isDarkTheme);

                if (!summaryStatus.isShow() && this.chartModeStyle == ChartModeStyle.LeftAndRightLabels) {
                    this.afterAdjustPreviewModeChart();
                    setTimeout(() => {
                        this.base.element?.findFirst('.chart-container')?.removeClass('no-opacity');
                    }, 150);
                }

                summaryStatus.show();
            };

            if (this.chartModeStyle != ChartModeStyle.LeftAndRightLabels || !summaryStatus.isShow() || (isResize && this.chartModeStyle == ChartModeStyle.LeftAndRightLabels)) {
                let percentage = this.previewSymbolSizePercent;
                if (this.chartModeStyle == ChartModeStyle.LeftAndRightLabels) {
                    percentage = 80;
                }

                summaryStatus.scaleElementPreviewMode(percentage, this.previewChartLabelPosition, this.chartModeStyle == ChartModeStyle.LeftAndRightLabels, isResize);
            }

            render();
        });
    }

    public renderDebugLabel() {
        if (!this.trace) {
            return;
        }

        const highestHigh = FormatHelpers.numberToString(this.highestHighPoint?.high ?? 0, 2)
        const yesterdayClose = FormatHelpers.numberToString(this.pointFromYesterday?.close ?? 0, 2)
        const todayClose = FormatHelpers.numberToString(this.xAxisPlotLinePoint?.close ?? 0, 2)
        const todayOpen = FormatHelpers.numberToString(this.xAxisPlotLinePoint?.open ?? 0, 2)
        this.base.tiChartSeries.element.findFirst('.debug-label')?.removeClass('hidden');
        this.base.tiChartSeries.element.findFirst('.debug-label')?.setInnerHtml(`
            <p>Yesterday Close: ${yesterdayClose}</p>
            <p>Today Close: ${todayClose}</p>
            <p>Today Open ${todayOpen}</p>
            <p>Today Highest Hight ${highestHigh}</p>`)
    }

    private resizeTimer: any;
    private clientWidth = document.body.clientWidth;
    private onResize() {
        this.base.domEvents.onResize(this.id, () => {
            if (this.clientWidth != document.body.clientWidth) {
                this.clientWidth = document.body.clientWidth;

                if (this.chartMode == ChartMode.Preview && this.chartModeStyle == ChartModeStyle.LeftAndRightLabels) {
                    this.base.element?.findFirst('.chart-container')?.addClass('no-opacity');
                }

                clearTimeout(this.resizeTimer);
                this.resizeTimer = setTimeout(() => {
                    this.adjustPreviewModeContainer(true);
                }, 150);
            }
        });
    }

    private adjustPreviewModeContainer(isResize: boolean) {
        if (this.chartMode != ChartMode.Preview) {
            return;
        }

        this.updateMarketingSummaryStatusLine(isResize);
        this.afterAdjustPreviewModeChart();

        if (isResize && this.chartModeStyle == ChartModeStyle.LeftAndRightLabels) {
            setTimeout(() => {
                this.base.element?.findFirst('.chart-container')?.removeClass('no-opacity');
            }, 500);
        }
    }

    private afterAdjustPreviewModeChart() {
        if (this.base.tiChartSeries?.highStockChart) {
            const yAxis = this.base.tiChartSeries?.highStockChart?.yAxis[0];
            yAxis?.update({
                height: null
            }, true);

            this.base.tiChartSeries?.highStockChart.update({
                chart: {
                    height: null
                }
            });

            setTimeout(() => {
                this.base.tiChartSeries?.highStockChart.update({
                    chart: {
                        height: this.base.tiChartSeries.element.clientHeight
                    }
                });

                this.base.tiChartSeries?.highStockChart.reflow();
            }, 150);

            setTimeout(() => {
                this.setPreviewModeXAxisPointPlotLine(true);
                setTimeout(() => {
                    this.setPreviewModeXAxisPointPlotLine();
                }, 250);
            }, 200);
        }
    }

    private getRowValue(row: Map<string, string>, columnName: string) {
        if (row.get(columnName))
            return row.get(columnName);
        else
            return '';
    }

    public override subscribeInitChartIndicatorsData(callback: Function) {
        this.subscribeInitChartIndicatorsDataCallbacks.push(callback);
    }

    private onSubscribeInitChartIndicatorsData(model: Array<TIIndicatorDataModel>) {
        for (const callback of this.subscribeInitChartIndicatorsDataCallbacks) {
            callback(model);
        }
    }

    public override subscribeInitChartIndicatorsTIQData(callback: Function) {
        this.subscribeInitChartIndicatorsTIQDataCallbacks.push(callback);
    }

    private onSubscribeInitChartIndicatorsTIQData(model: Array<any>) {
        for (const callback of this.subscribeInitChartIndicatorsTIQDataCallbacks) {
            callback(model);
        }
    }

    public override subscribeInitChartComparisonsData(callback: Function) {
        this.subscribeInitChartComparisonsDataCallbacks.push(callback);
    }

    public override subscribeInitChartDrawingTools(callback: Function) {
        this.subscribeInitChartDrawingToolsCallbacks.push(callback);
    }

    private onSubscribeInitChartDrawingTools() {
        try {
            const drawingToolData = localStorage.getItem(this.localStorageDrawingToolsKey);
            TraceLoggingHelpers.log('subscribeInitChartDrawingTools :', drawingToolData);

            let model: any = [];

            if (drawingToolData && drawingToolData) {
                model = JSON.parse(drawingToolData);
            }

            // TraceLoggingHelpers.log('subscribeInitChartDrawingTools parsed model :', model , this.subscribeInitChartDrawingToolsCallbacks);
            for (const callback of this.subscribeInitChartDrawingToolsCallbacks) {
                callback(this.symbol, this.timeframe, model);
            }
        } catch (ex: any) {
            console.error('onSubscribeInitChartDrawingTools Error', ex);
        }
    }

    public override subscribeInitChartDrawingToolSettings(callback: Function) {
        this.subscribeInitChartDrawingToolSettingsCallbacks.push(callback);
    }

    private onSubscribeInitChartDrawingToolSettings() {
        try {
            const drawingToolSettingData = localStorage.getItem(this.localStorageDrawingToolsSettings);
            TraceLoggingHelpers.log('subscribeInitChartDrawingToolSettings :', drawingToolSettingData);

            let model = new DrawingToolsSettingModel();

            if (drawingToolSettingData) {
                model = new DrawingToolsSettingModel(JSON.parse(drawingToolSettingData));
            }

            TraceLoggingHelpers.log('subscribeInitChartDrawingToolSettingsCallbacks parsed model :', model);
            for (const callback of this.subscribeInitChartDrawingToolSettingsCallbacks) {
                callback(model);
            }
        } catch (ex: any) {
            console.error('onSubscribeInitChartDrawingToolSettings Error', ex);
        }
    }

    public override subscribeUpdate10sRefreshChartData(callback: Function) {
        this.subscribeUpdate10sRefreshChartDataCallbacks.push(callback);
    }

    private onSubscribeUpdate10sRefreshChartData() {
        if (!this.symbol) {
            return;
        }

        const chartDataRequest = this.getChartDataRequest(2, undefined, this.newestDate);
        TraceLoggingHelpers.log('onSubscribeUpdate10sRefreshChartData ', chartDataRequest)

        const staringRequestTime = new Date();
        TraceLoggingHelpers.log(`web chart ${this.base.id} subscribeUpdate10sRefreshChartData`, staringRequestTime);

        this.chartDataClient.getChartData(chartDataRequest).then(response => {
            if (!response.isSuccess) {
                return;
            }

            let model = JSApiPointToUpdateTiChartDataPointMapper.map(response.data, this.lastPoint, this.showPreMarket, this.showPostMarket);

            if (model?.length) {
                this.lastPoint = model[model.length - 1];
                this.newestDate = this.lastPoint.start;

                this.username = !this.base.isDemo ? this.connection.getCurrentUser()?.username ?? 'demo' : 'demo';
                if (TimeframeHelper.timeframeIntraday(this.timeframe) || DateHelpers.isMarketOpenNow(this.base.isDemo)) {
                    // this.lastPrice = this.lastPoint.close;
                }

                if (model.length > 2) {
                    if (!this.isPnP && this.chartMode == ChartMode.Preview) {
                        this.setPointFromYesterdayClose(model, this.lastPoint);
                        model = this.getPointsFromYesterdayClose(model);
                    } else if (this.isPnP && this.chartMode == ChartMode.Standard) {
                        // this.base.changeFromClosePipChartLastBarClose = this.lastPoint.close;
                        // if (this.base.tiChartSeries.isCurrentBarVisible()) {
                        //     this.calcCFCPip();
                        // }
                    }
                }

                this.updateMarketingSummaryStatusLine(false);
            }

            const finishedRequestTime = new Date();

            TraceLoggingHelpers.log(`web chart ${this.base.id} subscribeUpdate10sRefreshChartData response: `, model)
            TraceLoggingHelpers.log(`web chart ${this.base.id} subscribeUpdate10sRefreshChartData total request time: ${((finishedRequestTime.valueOf() - staringRequestTime.valueOf()) / 1000)}s`)
            for (const callback of this.subscribeUpdate10sRefreshChartDataCallbacks) {
                callback(model);
            }

            const emaSettings = this.displayEMACloudIndicator && this.emaCloudSettings?.items?.length ? this.emaCloudSettings : null;
            const tiIndicatorsData = this.indicatorManager.mapTiIndicatorPoints(model, emaSettings)

            // this.onSubscribeLastPriceData();

            this.onSubscribeUpdate10sRefreshIndicatorsData(tiIndicatorsData);
            this.onSubscribeInitChartDrawingTools();

            this.populatePnl();
            this.popuplateOrderExecutionData();
            this.popuplateTradeOrderData();

            this.set10sRefreshTimer();

            if (!this.isPnP && this.chartMode == ChartMode.Preview) {
                setTimeout(() => {
                    this.setPreviewModeXAxisPointPlotLine();
                    // if (this.onPreviewChartInitWith10sRefreshCallback) {
                    //     this.onPreviewChartInitWith10sRefreshCallback();
                    //     this.onPreviewChartInitWith10sRefreshCallback = null;
                    // }
                }, 50);
            }
        });
    }

    public override subscribeUpdate10sRefreshIndicatorsData(callback: Function) {
        this.subscribeUpdate10sRefreshIndicatorsDataCallbacks.push(callback);
    }

    private onSubscribeUpdate10sRefreshIndicatorsData(model: Array<TIIndicatorDataModel>) {
        for (const callback of this.subscribeUpdate10sRefreshIndicatorsDataCallbacks) {
            callback(model);
        }
    }

    public override subscribeUpdateChartHistoryData(callback: Function) {
        this.subscribeUpdateChartHistoryDataCallbacks.push(callback)
    }

    private requestHistoryDataCount = 0;
    private onSubscribeUpdateChartHistoryData() {
        const model: Array<TIChartPointModel> = [];
        if (!this.symbol || !this.oldestDate) {
            for (const callback of this.subscribeUpdateChartHistoryDataCallbacks) {
                callback(model);
            }
            return;
        }

        const chartDataRequest = this.getChartDataRequest(350, this.oldestDate);
        this.chartDataClient.getChartData(chartDataRequest).then(response => {
            if (!response.isSuccess) {
                return;
            }

            this.requestHistoryDataCount++;
            let items = response.data.get(this.symbol);
            const model = JSApiPointToTiChartDataPointMapper.map(response.data, (((items.grid?.length ?? 0) - 2) * this.requestHistoryDataCount) * -1, this.showPreMarket, this.showPostMarket);

            if (model?.length) {
                const firstPoint = model[0];
                this.oldestDate = firstPoint.start;
            }

            for (const callback of this.subscribeUpdateChartHistoryDataCallbacks) {
                callback(model);
            }

            const emaSettings = this.displayEMACloudIndicator && this.emaCloudSettings?.items?.length ? this.emaCloudSettings : null;
            const tiIndicatorsData = this.indicatorManager.mapTiIndicatorPoints(model, emaSettings)
            this.onSubscribeUpdateHistoryIndicatorsData(tiIndicatorsData);
        });
    }

    private getChartDataRequest(rowCount: number, startBefore?: Date, sinceDate?: Date): ChartDataRequestModel {
        const chartDataRequest = new ChartDataRequestModel();
        chartDataRequest.symbols = [this.symbol];
        chartDataRequest.startBefore = startBefore;
        chartDataRequest.sinceDate = sinceDate;

        if (TimeframeHelper.timeframeIntraday(this.timeframe)) {
            const minutesPerCandle: any = TimeframeHelper.getIntradayChartTimeFrameMinutePerCandle(this.timeframe);
            chartDataRequest.options = {
                timeFrame: 'intraday',
                rowCount: rowCount,
                minutesPerCandle: minutesPerCandle,
                atTime: startBefore || sinceDate
            };

            if (this.showPreMarket) {
                chartDataRequest.options.startOffset = this.preMarketMinutes;
            }

            if (this.showPostMarket) {
                chartDataRequest.options.endOffset = this.postMarketMinutes;
            }
        } else {
            const candleSize: any = TimeframeHelper.getDailyChartTimeFrameCandleSize(this.timeframe);
            chartDataRequest.options = {
                timeFrame: 'daily',
                rowCount: chartDataRequest.rowCount,
                candleSize: candleSize,
                atTime: startBefore || sinceDate
            };
        }
        chartDataRequest.extraRowCount = 1;

        const emaClondSettings = this.displayEMACloudIndicator && this.emaCloudSettings?.items?.length ? this.emaCloudSettings.items : [];
        this.indicatorManager.setChartDataRequestIndicators(chartDataRequest, emaClondSettings);
        return chartDataRequest;
    }

    public override subscribeUpdateHistoryIndicatorsData(callback: Function) {
        this.subscribeUpdateHistoryIndicatorsDataCallbacks.push(callback);
    }

    private onSubscribeUpdateHistoryIndicatorsData(model: Array<TIIndicatorDataModel>) {
        for (const callback of this.subscribeUpdateHistoryIndicatorsDataCallbacks) {
            callback(model);
        }
    }

    public override subscribeUpdateChartHistoryComparisonData(callback: Function) {
        this.subscribeUpdateChartHistoryComparisonDataCallbacks.push(callback);
    }

    public override subscribeRealtimeData(callback: Function) {
        this.subscribeRealtimeDataCallbacks.push(callback);
    }

    public setMainChartLineAndFillColor(lineHexColor: string, areaFillHexColor: string, gradientHexColor: string) {
        if (!this.isPnP && this.chartMode == ChartMode.Preview) {
            // TraceLoggingHelpers.log('set setMainChartLineAndFillColor ', lineHexColor, areaFillHexColor, gradientHexColor)
            if (lineHexColor) {
                this.chartStyleModel.selectedLineColor = new ChartStyleColorModel(color.HEX2RGB(lineHexColor));
            }
            if (areaFillHexColor) {
                this.chartStyleModel.marketingChartAreaFillHexColor = new ChartStyleColorModel(color.HEX2RGB(areaFillHexColor));
            }
            if (gradientHexColor) {
                const gradientColor = new ChartStyleColorModel(color.HEX2RGB(gradientHexColor));
                let gradientHex = color.HEX2RGB(gradientColor.lighten(65));
                if (this.theme == ChartTheme.Dark) {
                    gradientHex = color.HEX2RGB(gradientColor.darken(65));
                }
                this.chartStyleModel.marketingChartGradientHexColor = new ChartStyleColorModel(gradientHex);
            }
        }
    }

    public setPipChartLineAndFillColorFromTopListChangesFromClose() {
        if (!this.isPnP || this.base.changeFromClosePercentagePipChart == null) { return; }

        this.setChartStyleSettingsPreviewUpDownColors(this.chartStyleModel);
        this.chartStyleModel.chartTypeLineWidth = 2;

        this.base.setPipChartStyleWithChangesFromClose(this.chartStyleModel)
    }

    public setChartMode(chartMode: ChartMode) {
        this.chartMode = chartMode;
        if (this.chartMode == ChartMode.Preview) {
            this.base.toggleChartToPreviewMode(true);
        } else {
            this.base.toggleChartToPreviewMode(false);
        }

        if (this.chartMode == ChartMode.PopupChart && this.baseElement?.parentElement) {
            this.baseElement.parentElement.classList.add('extra-small');
            this.base.element?.parentElement?.classList.add('extra-small');
        }

        this.chartStyleModel.pnpEnabled = this.chartMode == ChartMode.Standard;
        const summaryStatusLines = this.base.getComponentsByType(MarketingWebChartSummaryStatusLineComponent) as Array<MarketingWebChartSummaryStatusLineComponent>;
        if (!this.isPnP) {
            if (this.chartMode == ChartMode.Preview) {
                this.showPreMarket = false;
                this.showPostMarket = false;
                this.displayVolumeIndicator = false;
                this.chartType = ChartType.Area;

                if (this.base.isPreview) {
                    const isAfterEleven = DateHelpers.isNowMarketTimeZoneAfterHousrs(11);
                    const isAfterNoon = DateHelpers.isNowMarketTimeZoneAfterHousrs(12);
                    const isAfterTwoPM = DateHelpers.isNowMarketTimeZoneAfterHousrs(14);

                    this.timeframe = Timeframe.OneMinute;

                    if (this.chartModeStyle == ChartModeStyle.LeftAndRightLabels) {
                        if (isAfterTwoPM) {
                            this.timeframe = Timeframe.TenMinute;
                        } else if (isAfterNoon) {
                            this.timeframe = Timeframe.FiveMinute;
                        } else if (isAfterEleven) {
                            this.timeframe = Timeframe.TwoMinute;
                        }
                    } else {
                        if (isAfterTwoPM) {
                            this.timeframe = Timeframe.FiveMinute;
                        } else if (isAfterNoon) {
                            this.timeframe = Timeframe.TwoMinute;
                        }
                    }

                    TraceLoggingHelpers.log('timeframe ', this.timeframe)
                } else {
                    this.timeframe = Timeframe.Day;
                }

                this.chartStyleModel.chartType = this.chartType;
                this.chartStyleModel.isOneColor = true;

                this.chartStyleModel.selectedLineColor = new ChartStyleColorModel({ r: 90, g: 185, b: 116 });
                this.chartStyleModel.isGradientAreaColorChecked = true;

                const lineColor1 = this.chartStyleModel.selectedLineColor.lighten(50);
                const lineColor2 = new ChartStyleColorModel(color.HEX2RGB(lineColor1));

                this.chartStyleModel.marketingChartAreaFillHexColor = this.chartStyleModel.selectedLineColor;
                this.chartStyleModel.marketingChartGradientHexColor = lineColor2;

                if (this.chartStyleModel.isDarkTheme) {
                    this.chartStyleModel.selectedChartGradientColor = this.chartStyleModel.selectedChartBackgroundColor;
                } else {
                    this.chartStyleModel.selectedChartBackgroundColor = new ChartStyleColorModel({ r: 255, g: 255, b: 255 });
                    this.chartStyleModel.selectedChartGradientColor = new ChartStyleColorModel({ r: 255, g: 255, b: 255 });
                }

                if (this.chartMode == ChartMode.Preview && summaryStatusLines?.length) {

                    this.updateMarketingSummaryStatusLine(false);

                    summaryStatusLines[0].show();

                    setTimeout(() => {
                        this.adjustPreviewModeContainer(false);
                    }, 200);
                }
            } else {
                this.chartType = ChartType.Candlestick;
                this.chartStyleModel = this.getChartStyleSettings(this.theme);

                const timeframeQuery = this.getFromQueryStringOrDataAttribute('timeframe');
                if (timeframeQuery) {
                    const timeframe = TimeFrameToChartTimeFrameMapper.map(timeframeQuery);
                    if (timeframe >= 0) {
                        this.timeframe = timeframe;
                    }
                }

                const displayVolumeIndicatorQuery = this.getFromQueryStringOrDataAttribute('displayvolumeindicator');
                if (displayVolumeIndicatorQuery) {
                    this.displayVolumeIndicator = displayVolumeIndicatorQuery.toLowerCase() === 'true';
                }

                const showPreMarketQuery = this.getFromQueryStringOrDataAttribute('showpremarket');
                if (showPreMarketQuery) {
                    this.showPreMarket = showPreMarketQuery.toLowerCase() === 'true';
                }

                const showPostMarketQuery = this.getFromQueryStringOrDataAttribute('showpostmarket');
                if (showPostMarketQuery) {
                    this.showPostMarket = showPostMarketQuery.toLowerCase() === 'true';
                }

                if (summaryStatusLines?.length) {
                    summaryStatusLines[0].hide();
                }
            }
        } else {
            if (this.chartMode == ChartMode.Standard) {
                this.setPipChartLineAndFillColorFromTopListChangesFromClose();
            }
        }
    }

    public setChartPreviewSymbolSizePercent(percentage: number) {
        this.previewSymbolSizePercent = percentage;
    }

    public override subscribeChartStyleData(callback: Function) {
        this.subscribeChartStyleDataCallbacks.push(callback);
    }

    public setChartStyleData(theme: ChartTheme) {
        this.theme = theme;
        this.chartStyleModel = this.getChartStyleSettings(this.theme);
        this.setChartMode(this.chartMode);
    }

    public setChartType(type: ChartType) {
        this.chartType = type;
        this.chartStyleModel = this.getChartStyleSettings(this.theme);
    }

    private setChartStyleSettingsPreviewUpDownColors(themeModel: ChartStyleSettingsModel) {
        if (themeModel.selectedAreaChartUpColor) {
            themeModel.selectedUpCandleColor = themeModel.selectedAreaChartUpColor;
        } else {
            if (themeModel.isDarkTheme) {
                themeModel.selectedUpCandleColor = new ChartStyleColorModel({
                    r: 40,
                    g: 57,
                    b: 40
                });
            } else {
                themeModel.selectedUpCandleColor = new ChartStyleColorModel({
                    r: 25,
                    g: 132,
                    b: 25
                });
            }
        }

        if (themeModel.selectedAreaChartDownColor) {
            themeModel.selectedDownCandleColor = themeModel.selectedAreaChartDownColor;
        } else {
            if (themeModel.isDarkTheme) {
                themeModel.selectedUpCandleColor = new ChartStyleColorModel({
                    r: 225,
                    g: 121,
                    b: 121
                });
            } else {
                themeModel.selectedUpCandleColor = new ChartStyleColorModel({
                    r: 86,
                    g: 28,
                    b: 28
                });
            }
        }

        themeModel.chartTypeLineWidth = 4;

        if (this.base.element.clientWidth <= 350) {
            themeModel.chartTypeLineWidth = 2;
        }

        if (this.base.element.clientWidth <= 125) {
            themeModel.chartTypeLineWidth = 1;
        }
    }

    private getChartStyleSettings(theme: ChartTheme): ChartStyleSettingsModel {
        const themeModel = WebchartThemeManager.getInstance().getChartThemeByName(theme, this.isPnP, this.chartType, this.showPostMarket, this.showPreMarket, this.pnpEnabled, this.chartMode);
        if (!themeModel) {
            return new ChartStyleSettingsModel();
        }

        if (this.chartMode == ChartMode.Preview) {
            this.setChartStyleSettingsPreviewUpDownColors(themeModel);
            themeModel.zoomLevel = 0;

            if (this.chartModeStyle == ChartModeStyle.LeftAndRightLabels) {
                themeModel.chartTypeLineWidth = 1;
            }

        } else {
            if (this.chartMode == ChartMode.Standard && this.isPnP) {
                this.setChartStyleSettingsPreviewUpDownColors(themeModel);
            }

            const userPersistingZoomLevel = this.getZoomLevel();
            if (userPersistingZoomLevel != null) {
                themeModel.zoomLevel = userPersistingZoomLevel;
            } else {
                themeModel.zoomLevel = this.mainChartZoomLevel;
            }

            themeModel.highChartIndicatorsEnabled = true;
            themeModel.emojiDrawingToolsEnabled = true;
            themeModel.emojiDefaultSize = 32;
        }

        const lineColor1 = themeModel.selectedLineColor.lighten(50);
        const lineColor2 = new ChartStyleColorModel(color.HEX2RGB(lineColor1));
        themeModel.marketingChartAreaFillHexColor = themeModel.selectedLineColor;
        themeModel.marketingChartGradientHexColor = lineColor2;

        if (this.isPnP) {
            themeModel.zoomLevel = this.pipChartZoomLevel;
            themeModel.highChartIndicatorsEnabled = false;
            themeModel.emojiDrawingToolsEnabled = false;
        }

        return themeModel;
    }

    public onSubscribeChartStyleData() {
        const model = new ChartStyleSettingsModel(ObjectHelpers.copy(this.chartStyleModel));

        model.zoomEnabled = this.zoomEnabled;
        model.mobileZoomEnabled = this.mobileZoomEnabled;
        model.marketingChartTimeframeAutoExpand = this.marketingChartTimeframeAutoExpand;
        model.exitOrderDisplayBluePlotline = this.exitOrderDisplayBluePlotline;
        model.rightEdgePaddingSaveEnabled = this.saveRightAddPadding;
        model.futureScrollingEnabled = this.futureScrollingEnabled;
        model.futureScrollingBarCount = this.futureScrollingBarCount;
        model.futureScrollingMinDisplayBarCount = this.futureScrollingMinDisplayBarCount;
        model.futureScrollingScrollStopEnabled = this.futureScrollingScrollStopEnabled;

        if (this.futureScrollingEnabled) {
            model.supportsFutureScrolling = true;
        }

        const highChartIndicators = this.getSaveHighChartIndicators();
        model.highChartIndicators = highChartIndicators;
        model.extraFutureBarCount = this.getExtraFutureBarCount();
        model.singleStockState = this.getSingleStockWindowState();
        model.singleStockNoBackground = this.sswNoBackground;
        model.singleStockBGPercent = this.sswBackgroundPercent;
        model.singleStockSeparatorDarkColor = this.sswSeparatorDarkColor;
        model.singleStockSeparatorLightColor = this.sswSeparatorLightColor;
        model.changeFromCloseUpColorLight = this.cfcUpColorLight;
        model.changeFromCloseDownColorLight = this.cfcDownColorLight;
        model.changeFromCloseUpColorDark = this.cfcUpColorDark;
        model.changeFromCloseDownColorDark = this.cfcDownColorDark;
        model.lastPriceUpColorLight = this.lastPriceUpColorLight;
        model.lastPriceDownColorLight = this.lastPriceDownColorLight;
        model.lastPriceUpColorDark = this.lastPriceUpColorDark;
        model.lastPriceDownColorDark = this.lastPriceDownColorDark;
        model.rightEdgePaddingBarsDefault = this.rightEdgePaddingBarsDefault;

        model.pnpChangePercentEnabled = this.pipChangePercentEnabled;
        model.crossHairsEnabled = this.base.isPreview ? false : this.crosshairEnabled;
        // model.defaultEnableTradeOrderEntry = this.newTradeUIEnabled;

        model.emaCloudEnabled = this.emaCloudEnabled;
        model.emaCloudSectionCount = this.emaCloudSectionCount;
        model.emaCloudDisplayBuySellFlags = typeof this.emaCloudSettings?.displayBuySellFlags != 'undefined' ? this.emaCloudSettings.displayBuySellFlags : true;
        model.emaCloudDisplayOnlyLastTwoTrades = typeof this.emaCloudSettings?.displayOnlyLastTwoTrades != 'undefined' ? this.emaCloudSettings.displayOnlyLastTwoTrades : true;

        model.zoomAndScrollEnabled = this.zoomAndScrollEnabled;
        model.zoomAndScrollAbsoluteHeight = this.zoomAndScrollAbsoluteHeight;

        model.persistPositionShareQuantity = this.persistPositionShareQuantity;

        model.yAxisPanningEnabled = this.yAxisPanningEnabled;
        model.chartSettingsPopupEnabled = true;
        model.tradeOrderEntrySupported = true;
        model.tradeOrderEntryEnabled = true;
        model.plusMenuEnabled = true;
        model.webBasedIndicatorManagerSupported = true;
        model.totalVolumeDisplayTopRight = true;

        const pipKey = this.getPIPKeyStorage();
        if (pipKey) {
            const isValueExist = localStorage.getItem(pipKey);
            if (isValueExist) {
                model.pnpExpanded = false;
            } else {
                model.pnpExpanded = true;
            }
        }

        const tradeUIState = this.getTradeUIState();
        if (tradeUIState) {
            model.newTradeUIOpen = this.base.isDemo || (typeof liveData != 'undefined' && !liveData) ? false : tradeUIState.open;
            model.newTradeUITop = tradeUIState.top;
            model.newTradeUILeft = tradeUIState.left;
            model.newTradeUIType = tradeUIState.quoteType;
            // model.newTradeUIQuantity = tradeUIState.quoteQuantity;
            model.newTradeUITIF = tradeUIState.tif;
            model.newTradeUIAccount = tradeUIState.account;
        }

        model.isPreMarketDisabled = this.isPreMarketDisabled;
        model.isPostMarketDisabled = this.isPostMarketDisabled;

        model.chartSettingsPopupModel = this.getChartSettingsPopupModel();
        model.previewChartBackgroundTrasparent = this.previewChartBackgroundTrasparentMode;

        for (const callback of this.subscribeChartStyleDataCallbacks) {
            callback(model);
        }
    }

    public override subscribeCompanyLogoData(callback: Function) {
        this.subscribeCompanyLogoDataCallbacks.push(callback);
    }

    public override subscribeCurrentPositionData(callback: Function) {
        this.subscribeCurrentPositionDataCallbacks.push(callback);
    }

    public override subscribeCurrentPositionViewModel(callback: Function) {
        this.subscribeCurrentPositionViewModelCallbacks.push(callback);
    }

    public override subscribeChartRequirementsData(callback: Function) {
        // TraceLoggingHelpers.log('subscribeChartRequirementsData')
        this.subscribeChartRequirementsDataCallbacks.push(callback);
    }

    public override subscribeChartAllowBuyData(callback: Function) {
        this.subscribeChartAllowBuyDataCallbacks.push(callback);
    }

    public override subscribeChartAllowSellData(callback: Function) {
        this.subscribeChartAllowSellDataCallbacks.push(callback);
    }

    public override subscribeDrawTradeOrdersData(callback: Function) {
        this.subscribeDrawTradeOrdersDataCallbacks.push(callback);
    }

    private tradePositions: Array<TradePositionDataModel> = [];
    private setupSubscription_TradePositionsData(rawData: Array<BrokerPosition>) {
        const tradePositions: Array<TradePositionDataModel> = [];
        if (rawData?.length) {
            for (const data of rawData) {
                tradePositions.push(new TradePositionDataModel({
                    id: parseInt(data.AccountId),
                    accountId: data.AccountId,
                    symbol: data.Symbol,
                    averageCost: data.AverageCost,
                    lastPrice: data.LastPrice,
                    totalProfit: data.TotalProfit,
                    realizedProfitLoss: data.RealizedProfitLoss,
                    netSharesTradedToday: data.NetSharesTradedToday,
                    shares: data.Shares,
                    iChangeFromCloseDollar: data.iChangeFromCloseDollar,
                    iChangeFromClosePercent: data.iChangeFromClosePercent,
                    iLast: data.iLast,
                }));
            }
        }

        this.tradePositions = tradePositions.sort((x: TradePositionDataModel, y: TradePositionDataModel) => {
            if (x.id > y.id) {
                return 1;
            }

            if (x.id < y.id) {
                return -1;
            }

            return 0;
        });

        this.populatePnl();
        this.popuplateOrderExecutionData();
        this.popuplateTradeOrderData();
    }

    private temporaryPositions: Array<CurrentPositionViewModel> = [];
    public addTemporaryPosition(accountName: string, shareCount: number, averageCost: number) {
        this.temporaryPositions = [new CurrentPositionViewModel({
            accountId: -999,
            accountName: accountName,
            shares: shareCount,
            averageCost: averageCost,
            lastPrice: averageCost,
        })];

        this.populatePnl();
    }

    public clearTemporaryPosition() {
        this.temporaryPositions = [];
        this.populatePnl();
    }

    private populatePnl() {
        if (!this.papertTradeConnected) return;

        let currentPositions: Array<CurrentPositionViewModel> = [];
        if (this.temporaryPositions?.length) {
            for (const data of this.temporaryPositions) {
                currentPositions.push(new CurrentPositionViewModel({
                    accountId: data.accountId,
                    accountName: data.accountName,
                    symbol: this.symbol,
                    averageCost: data.averageCost,
                    lastPrice: data.lastPrice,
                    shares: data.shares,
                    netSharesTradedToday: data.netSharesTradedToday,
                    realizedProfitLoss: data.realizedProfitLoss,
                    temporary: true
                }));
            }
        }

        if (this.tradePositions?.length) {
            for (const data of this.tradePositions) {
                // if (data.symbol != this.symbol || data.shares == 0) continue;
                if (data.symbol != this.symbol) continue;
                const account = this.accounts.find((x => x.id + '' == data.accountId))
                currentPositions.push(new CurrentPositionViewModel({
                    accountId: data.accountId,
                    accountName: account?.name ?? '',
                    symbol: data.symbol,
                    averageCost: data.averageCost,
                    lastPrice: data.lastPrice,
                    shares: data.shares,
                    netSharesTradedToday: data.netSharesTradedToday,
                    realizedProfitLoss: data.realizedProfitLoss,
                    iChangeFromCloseDollar: data.iChangeFromCloseDollar,
                    iChangeFromClosePercent: data.iChangeFromClosePercent,
                    iLast: data.iLast,
                }));
            }
        }

        for (const callback of this.subscribeCurrentPositionViewModelCallbacks) {
            callback(currentPositions);
        }

        this.setupSubscription_SingleStockWindowData(this.singleStockWindowModel);
        this.onSubscribeLastPriceData();

        setTimeout(() => {
            const orderExecution = this.base.getComponentsByType(DrawOrderExecutionComponent)[0] as DrawOrderExecutionComponent;
            orderExecution?.enable();

            const tradeOrders = this.base.getComponentsByType(DrawTradeOrdersComponent)[0] as DrawTradeOrdersComponent;
            tradeOrders?.enable();
        }, 1800);
    }

    private tradeOrders: Array<TradeOrderDataModel> = [];
    private brokerOrders: Array<BrokerOrder> = [];
    private setupSubscription_TradeOrderData(rawData: Array<BrokerOrder>) {
        this.brokerOrders = rawData;
        this.popuplateOrderExecutionData();
        this.popuplateTradeOrderData();
    }

    private popuplateTradeOrderData() {
        if (!this.papertTradeConnected) return;

        TraceLoggingHelpers.log('brokerExecutions popuplateTradeOrderData', this.brokerOrders)
        const tradeOrders: Array<TradeOrderDataModel> = [];
        if (this.brokerOrders?.length) {
            for (const data of this.brokerOrders) {
                if (data.Symbol != this.symbol) continue;

                if (data.Status?.toLowerCase() == "cancelled" || data.Status?.toLowerCase() == "rejected") continue;

                const account = this.accounts.find((x => x.id + '' == data.AccountId))
                const orderPrice = data.StopPrice > 0 ? data.StopPrice : data.LimitPrice;
                const currentPosition = this.tradePositions.find((x: TradePositionDataModel) => x.symbol == data.Symbol);
                const positionInfo = this.tradePositions.find((x: TradePositionDataModel) => x.accountId == data.AccountId && x.symbol == data.Symbol);
                const filled = data.Status?.toLowerCase() == "filled";

                tradeOrders.push(new TradeOrderDataModel({
                    id: data.OrderId,
                    accountName: account?.name,
                    price: orderPrice,
                    stopPrice: data.StopPrice,
                    limitPrice: data.LimitPrice,
                    shares: data.Shares,
                    allowMoving: !filled,
                    currentShares: currentPosition?.shares,
                    isLong: data.IsLong,
                    isEntryOrder: data.IsEntryOrder ?? null,
                    statusText: data.Status,
                    ocaGroup: data.OcaGroup,
                    tradeType: data.TradeType,
                    goodAfterTime: data.GoodAfterTime,

                    entryTime: data.Created ?? null,
                    created: data.Created,
                    updated: data.Updated,

                    entryPrice: positionInfo?.averageCost,
                    positionInfoShares: positionInfo?.shares,

                    color: this.getOrderLineColor(data.Status, data.IsLong)
                }));
            }
        }

        this.tradeOrders = tradeOrders;

        TraceLoggingHelpers.log('tradeOrderData: ', this.brokerOrders)

        for (const callback of this.subscribeDrawTradeOrdersDataCallbacks) {
            callback(this.tradeOrders);
        }
    }

    private getOrderLineColor(status: string, buy: boolean): ChartStyleColorModel {
        const orderLineColorBuyFilled = new ChartStyleColorModel({ r: 0, g: 0, b: 255 });
        const orderLineColorBuy = new ChartStyleColorModel({ r: 0, g: 0, b: 255 });
        const orderLineColorSellFilled = new ChartStyleColorModel({ r: 255, g: 0, b: 0 });
        const orderLineColorSell = new ChartStyleColorModel({ r: 255, g: 0, b: 0 });

        if (buy) {
            if (status?.toLowerCase() == "filled")
                return orderLineColorBuyFilled;
            else
                return orderLineColorBuy;
        } else {
            if (status?.toLowerCase() == "filled")
                return orderLineColorSellFilled;
            else
                return orderLineColorSell;
        }
    }

    private orderExecutions: Array<DrawOrderExecutionModel> = [];
    private brokerExecutions: Array<BrokerExecution> = [];
    private setupSubscription_OrderExecutionData(rawData: Array<BrokerExecution>) {
        TraceLoggingHelpers.log('brokerExecutions', rawData)
        this.brokerExecutions = rawData;
        this.popuplateOrderExecutionData();
        this.popuplateTradeOrderData();
    }

    private popuplateOrderExecutionData() {
        if (!this.papertTradeConnected) return;

        this.orderExecutions = [];
        if (this.brokerExecutions?.length) {

            const executions = this.brokerExecutions.sort((a, b) => {
                if (a.Filled === null) return 1;
                if (b.Filled === null) return -1;
                return b.Filled.getTime() - a.Filled.getTime();
            });

            const buyOrders = executions.filter((x: BrokerExecution) => { return x.Side?.toLowerCase() == "buy" && x.Symbol == this.symbol });
            const sellOrders = executions.filter((x: BrokerExecution) => { return x.Side?.toLowerCase() == "sell" && x.Symbol == this.symbol });

            const buysByBar = this.getExecutionsByBar(buyOrders);
            for (const k in buysByBar) {
                this.getOrderExecutions(parseFloat(k), buysByBar[k]);
            }

            const sellsByBar = this.getExecutionsByBar(sellOrders);
            for (const k in sellsByBar) {
                this.getOrderExecutions(parseFloat(k), sellsByBar[k]);
            }
        }

        for (const callback of this.subscribeDrawOrderExecutionDataCallbacks) {
            callback(this.orderExecutions);
        }
    }

    private findExecutionsAverage(executions: Array<BrokerExecution>) {
        if (executions.length === 0) {
            return 0;
        }

        let sum = 0;
        for (const item of executions) {
            sum += item.FilledPrice;
        }

        return sum / executions.length;
    }

    private getOrderExecutions(barDateTime: number, executions: Array<BrokerExecution>) {
        let averagePrice = this.findExecutionsAverage(executions);
        let isLong = executions?.[0].Side?.toLowerCase() == "buy"
        let direction = "Bought";
        if (!isLong)
            direction = "Sold";

        let toolTip = "";
        let newLine = "";
        let symbol = "";

        const accounts: Array<TradeAccountModel> = [];
        for (const execution of executions) {
            const execId = execution.Id;
            const fillPrice = FormatHelpers.numberToStringThousandSeparators(execution.FilledPrice, true);
            const shares = execution.Shares;
            const fillTime = execution.Filled;

            symbol = execution.Symbol;
            toolTip += newLine + direction + " " + shares + " at $" + fillPrice + " at " + DateHelpers.format("yyyy-mm-dd HH:MM:SS", fillTime) + " exec id: " + execId;
            newLine = `&#13;&#10;`;

            const account = this.accounts.find(x => x.id + '' == execution.AccountId);
            if (account) {
                accounts.push(account);
            }
        }

        const isDarkTheme = this.chartStyleModel?.isDarkTheme ?? false;
        const exactBrowserEntryX = this.base.tiChartSeries.getCurrentXValueFromInitialDataPoints(barDateTime);
        const image = this.getOrderExecutionImage(isLong, isDarkTheme);
        this.orderExecutions.push(new DrawOrderExecutionModel({
            tooltip: toolTip,
            imageAnnotationName: image,
            symbol: symbol,
            x: parseFloat(exactBrowserEntryX.toFixed(2)),
            y: averagePrice,

            accounts: accounts
        }))
    }

    private getOrderExecutionImage(isLong: boolean, isDarkTheme: boolean) {
        if (isLong) {
            return isDarkTheme ? "EXECUTION_BUY_DARK" : "EXECUTION_BUY_LIGHT";
        } else {
            return isDarkTheme ? "EXECUTION_SHORT_DARK" : "EXECUTION_SHORT_LIGHT";
        }
    }

    private getExecutionsByBar(executions: Array<BrokerExecution>): any {
        const executionsByBar: any = {};
        if (executions) {
            for (const executionData of executions) {
                if (executionData.Filled) {
                    const todayOpen = DateHelpers.getMarketOpenInLocalTime(executionData.Filled);
                    const sinceOpen = executionData.Filled.valueOf() - todayOpen.valueOf();
                    const minutesSinceOpen = Math.round(sinceOpen / 60000);
                    let barDateTime = executionData.Filled;
                    if (TimeframeHelper.timeframeIntraday(this.timeframe)) {
                        const minutesPerCandle: any = TimeframeHelper.getIntradayChartTimeFrameMinutePerCandle(this.timeframe);
                        const barToday = (minutesSinceOpen / minutesPerCandle);
                        barDateTime = DateHelpers.addMinutes(todayOpen, barToday * minutesPerCandle);
                    }

                    let barExecutions: Array<BrokerExecution> = [];
                    if (executionsByBar[DateHelpers.toTimeT(barDateTime)]) {
                        barExecutions = executionsByBar[DateHelpers.toTimeT(barDateTime)];
                    }

                    barExecutions.push(executionData);
                    executionsByBar[DateHelpers.toTimeT(barDateTime)] = barExecutions;
                }
            }
        }

        return executionsByBar;
    }

    public override subscribeDrawOrderExecutionData(callback: Function) {
        this.subscribeDrawOrderExecutionDataCallbacks.push(callback);
    }

    public override subscribePriceAlertsData(callback: Function) {
        this.subscribePriceAlertsDataCallbacks.push(callback);
    }

    private setupSubscription_PriceAlertsData(model: Array<LimitAlertModel>) {
        for (const subscribe_PriceAlertsData of this.subscribePriceAlertsDataCallbacks) {
            subscribe_PriceAlertsData(model);
        }
    }

    public override subscribePriceAlertsImageData(callback: Function) {
        this.subscribePriceAlertsImageDataCallbacks.push(callback);
    }

    public override subscribeEarningNotificationData(callback: Function) {
        this.subscribeEarningNotificationDataCallbacks.push(callback);
    }

    private populateEarningNotification(singleStockWindowDataModel: SingleStockWindowDataModel) {
        if (!singleStockWindowDataModel || Number.isNaN(singleStockWindowDataModel.earningsDateUnixtime) || this.chartMode != ChartMode.Standard) {
            return;
        }

        const getMarketStatus = (ed: number) => {
            const result = (ed - Math.floor(ed)) * 100;
            switch (result) {
                case 25:
                    return "before market open";
                case 50:
                    return "during market hours";
                case 75:
                    return "after market close";
                case -25:
                    return "after market close";
                case -50:
                    return "during market hours";
                case -75:
                    return "before market open";
                default:
                    return "during market hours";
            }
        }

        const earningsDate = singleStockWindowDataModel.earningsDateUnixtime;
        let earningsBrowserNotification = ``;
        if (earningsDate < 7 && earningsDate > 0) {
            const businessDays = Math.trunc(earningsDate);
            if (businessDays == 0) {
                earningsBrowserNotification = "<p>Earnings</p>" + "<b> Today </b>" + "<p>" + getMarketStatus(earningsDate).replace("market", "mkt") + "</p>";
            } else {
                const now = DateHelpers.toMarketTimeZone(new Date()).toJSDate();
                const nextBusinessDays = DateHelpers.addBusinessDay(new Date(now.valueOf()), businessDays);
                const earningNextDay = DateHelpers.getMarketOpenInLocalTime(nextBusinessDays);
                const tomorrow = DateHelpers.addDays(new Date(now.valueOf()), 1);
                const isTomorrow = DateHelpers.format("dd-mm-yyyy", earningNextDay) == DateHelpers.format("dd-mm-yyyy", tomorrow);
                if (isTomorrow) {
                    earningsBrowserNotification = "<p>Earnings</p><b> Tomorrow </b><p>" + getMarketStatus(earningsDate).replace("market", "mkt") + "</p>";
                }
                else {
                    earningsBrowserNotification = "<p>Earnings in</p><b> " + businessDays + " " + (businessDays > 1 ? "days" : "day") + " </b> " + "<p>on</p><b> " + DateHelpers.format("SD mm", earningNextDay) + "/" + DateHelpers.format("dd", earningNextDay) + " </b><p>" + getMarketStatus(earningsDate).replace("market", "mkt") + "</p>";
                }
            }
        }

        for (const cb of this.subscribeEarningNotificationDataCallbacks) {
            cb(earningsBrowserNotification);
        }
    }

    public override subscribeHaltedNotificationData(callback: Function) {
        this.subscribeHaltedNotificationDataCallbacks.push(callback);
    }

    public override subscribeLastPriceData(callback: Function) {
        if (this.isPnP) {
            const chartContainer = this.base as any;
            chartContainer?.baseChart?.chartService?.subscribeLastPriceData(callback);
        } else {
            this.subscribeLastPriceDataCallbacks.push(callback);
        }
    }

    private onSubscribeLastPriceData() {
        if (this.isPnP || this.switchingSymbol) {
            return;
        }

        let lastPriceToSend = parseFloat(this.lastPrice + '');
        const openPosition = this.tradePositions.find(x => x.symbol == this.symbol && x.shares != 0);
        if (openPosition) {
            const iLast = parseFloat(openPosition.iLast + '');
            if (iLast != 0 && !Number.isNaN(iLast) && iLast != null) {
                lastPriceToSend = iLast;
            }
        }

        for (const callback of this.subscribeLastPriceDataCallbacks) {
            callback(lastPriceToSend);
        }

        this.onSubscribeRealtimeData(lastPriceToSend);
    }

    private onSubscribeRealtimeData(tradePrice: number) {
        if (!this.lastPoint || !this.tradeQuoteTradeTime) return;

        const lastBarStartTime = this.lastPoint.startMarketTimeZone.valueOf();
        const lastBarEndTime = this.lastPoint.endMarketTimeZone.valueOf();
        const now = new Date();
        const isMarketOpen = DateHelpers.isMarketOpenNow(this.base.isDemo);
        const isPreMarket = DateHelpers.isPremarket(now);
        const isPostMarket = DateHelpers.isPostmarket(now);

        const updateBrowserRealTimeTradeModel = new RealtimeTradeDataModel({
            close: tradePrice,
            high: this.lastPoint.high,
            low: this.lastPoint.low,
            open: this.lastPoint.open,
            start: this.lastPoint.start,
            end: this.lastPoint.end,
            startTimeT: this.lastPoint.startTimeT,
            endTimeT: this.lastPoint.endTimeT,
            symbol: this.lastPoint.symbol,
            xValue: this.lastPoint.xValue
        });
       
        if (isMarketOpen || isPreMarket || isPostMarket) {
            if (this.tradeQuoteTradeTime.valueOf() >= lastBarStartTime && this.tradeQuoteTradeTime.valueOf() <= lastBarEndTime) {
                if (tradePrice > this.lastPoint.high) {
                    updateBrowserRealTimeTradeModel.high = tradePrice;
                }

                if (tradePrice < this.lastPoint.low) {
                    updateBrowserRealTimeTradeModel.low = tradePrice;
                }
            } else if (this.tradeQuoteTradeTime.valueOf() > lastBarEndTime) {
                const barTimeBoundary = RealTimeTradeHelpers.getChartBarTimeBoundary(now, this.timeframe);
                const startTimeT = TIUtilities.toTimeT(barTimeBoundary.start);
                const endTimeT = TIUtilities.toTimeT(barTimeBoundary.end);

                updateBrowserRealTimeTradeModel.shouldCreateNewBar = true;
                updateBrowserRealTimeTradeModel.start = barTimeBoundary.start;
                updateBrowserRealTimeTradeModel.startTimeT = startTimeT;
                updateBrowserRealTimeTradeModel.end = barTimeBoundary.end;
                updateBrowserRealTimeTradeModel.endTimeT = endTimeT;
                updateBrowserRealTimeTradeModel.open = tradePrice;
                updateBrowserRealTimeTradeModel.high = tradePrice;
                updateBrowserRealTimeTradeModel.low = tradePrice;
                updateBrowserRealTimeTradeModel.close = tradePrice;
                updateBrowserRealTimeTradeModel.symbol = this.symbol;
                updateBrowserRealTimeTradeModel.xValue = this.lastPoint.xValue + 1;
            }
        }

        for (const callback of this.subscribeRealtimeDataCallbacks) {
            callback(updateBrowserRealTimeTradeModel);
        }
    }

    public override subscribeDrawTradePriceAnnotations(callback: Function) {
        this.subscribeDrawTradePriceAnnotationsCallbacks.push(callback);
    }

    public override subscribeSwingExitData(callback: Function) {
        this.subscribeSwingExitDataCallbacks.push(callback);
    }

    public override subscribeSmartRiskData(callback: Function) {
        this.subscribSmartRiskDataCallbacks.push(callback);
    }

    public override subscribeHeartStatusData(callback: Function) {
        this.subscribHeartStatusDataCallbacks.push(callback);
    }

    public override subscribeDrawEarningNotificationData(callback: Function) {
        this.subscribeDrawEarningNotificationDataCallbacks.push(callback);
    }

    private onSubscribeDrawEarningNotificationData(singleStockWindowDataModel: SingleStockWindowDataModel) {
        if (!singleStockWindowDataModel || Number.isNaN(singleStockWindowDataModel.earningsDateUnixtime) || singleStockWindowDataModel.earningsDateUnixtime >= 0 || this.chartMode != ChartMode.Standard) {
            return;
        }

        const pastEarningsDate = singleStockWindowDataModel.getEarningDate();
        const pastEarningsDateDecimal = parseInt(((singleStockWindowDataModel.earningsDateUnixtime - parseInt(singleStockWindowDataModel.earningsDateUnixtime + '')) * 100) + '');

        const now = new Date();
        TraceLoggingHelpers.log('now ', now)
        TraceLoggingHelpers.log('pastEarningsDate isPnP: ' + this.base.pnpMode, pastEarningsDate)
        // TraceLoggingHelpers.log('pastEarningsDateDecimal ', pastEarningsDateDecimal)

        if (Number.isNaN(pastEarningsDateDecimal) || !this.base.tiChartSeries.tiChartPoints.length) {
            for (const callback of this.subscribeDrawEarningNotificationDataCallbacks) {
                callback(null);
            }

            return;
        }

        const pastEarningsDateStr = DateHelpers.format('dd-mm-yyyy', pastEarningsDate);
        // TraceLoggingHelpers.log('pastEarningsDateStr ', pastEarningsDateStr)

        let model: DrawEarningNotificationDataModel = null;
        for (const dataPoint of this.base.tiChartSeries.tiChartPoints) {
            const pointStartDateStr = DateHelpers.format('dd-mm-yyyy', dataPoint.start);

            if (pointStartDateStr == pastEarningsDateStr) {
                TraceLoggingHelpers.log('pastEarningsDateStr match!! pointStartDateStr isPnP: ' + this.base.pnpMode, pointStartDateStr);
                TraceLoggingHelpers.log('pastEarningsDateStr match!! dataPoint isPnP: ' + this.base.pnpMode, dataPoint);
                let xValue = dataPoint.xValue;
                model = new DrawEarningNotificationDataModel({
                    pastEarningsDateDecimal: pastEarningsDateDecimal,
                    earningDate: pastEarningsDate,
                    xValue: xValue
                });

                break;
            }
        }

        for (const callback of this.subscribeDrawEarningNotificationDataCallbacks) {
            callback(model);
        }
    }

    public override subscribe_SingleStockWindowData(callback: Function) {
        this.subscribe_SingleStockWindowDatas.push(callback);
    }

    private setupSubscription_SingleStockWindowData(model: SingleStockWindowDataModel) {
            
        if (model?.symbol && this.tradeQuoteDollarChange != null && this.tradeQuotePercentChange != null && this.tradeQuoteVolume != null) {
            if (this.isNumberValid(this.tradeQuoteDollarChange)) {
                model.changesFromCloseDollar = this.tradeQuoteDollarChange;
            }
            if (this.isNumberValid(this.tradeQuotePercentChange)) {
                model.changesFromClosePercentage = this.tradeQuotePercentChange;
            }
            if (this.isNumberValid(this.tradeQuoteVolume)) {
                model.totalVolume = this.tradeQuoteVolume;
            }
        }

        const pnl = this.base.getComponentsByType(PnlComponent)[0] as PnlComponent;
        const openPosition = pnl?.getOpenPosition();
        const iChangeFromCloseDollar = openPosition ? parseFloat(openPosition.iChangeFromCloseDollar + '') : Number.NaN;
        const iChangeFromClosePercent = openPosition ? parseFloat(openPosition.iChangeFromClosePercent + '') : Number.NaN;
        const iChangeFromCloseValid = this.isNumberValid(iChangeFromCloseDollar) && this.isNumberValid(iChangeFromClosePercent);
        if (iChangeFromCloseValid) {
            model.changesFromCloseDollar = openPosition.iChangeFromCloseDollar;
            model.changesFromClosePercentage = openPosition.iChangeFromClosePercent;
        }

        for (const subscribe_SingleStockWindowData of this.subscribe_SingleStockWindowDatas) {
            subscribe_SingleStockWindowData(model);
        }
    }

    private isNumberValid(val: number | null) {
        return val != 0 && !Number.isNaN(val) && val != null && isFinite(val);
    }

    public override subscribe_CompanyProfileData(callback: Function) {
        this.subscribe_CompanyProfileDatas.push(callback);
    }

    private requestCompanyProfileData() {
        this.connection.companyProfileClient.getCompanyProfile(this.symbol).then((response: any) => {
            if (!response.isSuccess) {
                return;
            }

            const model = new CompanyProfileDataModel(response.data);
            for (const subscribe_CompanyProfileData of this.subscribe_CompanyProfileDatas) {
                subscribe_CompanyProfileData(model);
            }
        });
    }

    public override subscribeEmojiData(callback: Function) {
        this.subscribeEmojiDataCallbacks.push(callback);
    }

    private onSubscribeEmojiData() {
        const model = EmojiModel.makeList([
            {
                emoji: '🚀',
                label: 'Rocket',
            },
            {
                emoji: '💲',
                label: 'Dollar sign'
            },
            {
                emoji: '💰',
                label: 'Money bag'
            },
            {
                emoji: '💵',
                label: 'Dollar banknote'
            },
            {
                emoji: '📈',
                label: 'Chart increasing'
            },
            {
                emoji: '📉',
                label: 'Chart decreasing'
            },
            {
                emoji: '🤑',
                label: 'Money mouth face'
            },
            {
                emoji: '💸',
                label: 'Money with wings'
            },
            {
                emoji: '💎',
                label: 'Diamond'
            },
            {
                emoji: '🙌',
                label: 'Hands'
            },
            {
                emoji: '🐂',
                label: 'Bull'
            },
            {
                emoji: '🐻',
                label: 'Bear'
            }
        ]);
        // TraceLoggingHelpers.log('subscribeEmojiData :', model);
        for (const callback of this.subscribeEmojiDataCallbacks) {
            callback(model);
        }
    }

    public override subscribe_TradeAccounts(callback: Function) {
        this.subscribe_TradeAccountsDatas.push(callback);
    }

    private accounts: Array<TradeAccountModel> = [];
    private setupSubscription_TradeAccountsData(rawData: Array<any>) {
        const data: Array<any> = [];
        if (rawData?.length) {
            for (const acc of rawData) {
                data.push({
                    id: parseInt(acc.AccountId, 10),
                    name: acc.Name
                });
            }
        }

        this.accounts = TradeAccountModel.makeList(data);
        this.notifyTradeAccountSubscribers(this.accounts);
    }

    public override requestHistory() {
        this.onSubscribeUpdateChartHistoryData();
    }

    public override subscribe_SimpleTiDataPoints(callback: Function) {
        this.subscribe_SimpleTiDataPointsDatas.push(callback);
    }

    public override requestSimpleTiDataPoints(symbol: string, timeframe: Timeframe, preMarket: boolean, postMarket: boolean, rowCount: number) {
        this.onRequestSimpleTiDataPoints(symbol, timeframe, preMarket, postMarket, rowCount);
    }

    private onRequestSimpleTiDataPoints(symbol: string, timeframe: Timeframe, preMarket: boolean, postMarket: boolean, rowCount: number) {
        if (!symbol) {
            return;
        }

        const chartDataRequest = new ChartDataRequestModel();
        chartDataRequest.symbols = [symbol];
        chartDataRequest.rowCount = rowCount;

        if (TimeframeHelper.timeframeIntraday(timeframe)) {
            const minutesPerCandle: any = TimeframeHelper.getIntradayChartTimeFrameMinutePerCandle(timeframe);
            chartDataRequest.options = { timeFrame: 'intraday', rowCount: chartDataRequest.rowCount, minutesPerCandle: minutesPerCandle };
            if (preMarket && !this.isPreMarketDisabled) {
                chartDataRequest.options.startOffset = this.preMarketMinutes;
            }

            if (postMarket && !this.isPostMarketDisabled) {
                chartDataRequest.options.endOffset = this.postMarketMinutes;
            }
        } else {
            const candleSize: any = TimeframeHelper.getDailyChartTimeFrameCandleSize(timeframe);
            chartDataRequest.options = { timeFrame: 'daily', rowCount: chartDataRequest.rowCount, candleSize: candleSize };
        }

        this.chartDataClient.getChartData(chartDataRequest).then((response) => {
            if (!response.isSuccess) {
                return;
            }

            const model = JSApiPointToTiChartDataPointMapper.map(response.data, 0, false, false);
            TraceLoggingHelpers.log('getChartData onRequestSimpleTiDataPoints', { response: response, model: model });

            for (const cb of this.subscribe_SimpleTiDataPointsDatas) {
                cb(model, symbol, timeframe);
            }
        });
    }

    public addTemporaryStopLoss(data: TemporaryStopLossModel) {
        if (data) {
            data.symbol = this.symbol;
            for (const cb of this.subscribe_AddTemporaryStopLossDatas) {
                cb(data);
            }
        }
    }

    public override subscribe_AddTemporaryStopLoss(callback: Function): void {
        this.subscribe_AddTemporaryStopLossDatas.push(callback);
    }

    public override subscribe_OrderRejectionMessage(callback: Function) {
        this.subscribe_OrderRejectionMessageDatas.push(callback);
    }

    public notifyOrderRejectionMessage(data: string) {
        for (const callback of this.subscribe_OrderRejectionMessageDatas) {
            callback(data);
        }
    }

    public override requestNewSymbol(symbol: string) {
        const symbolChange = this.symbol != symbol;
        if (!symbolChange) {
            return;
        }

        this.symbol = symbol;

        if (!this.isPnP) {
            this.base.domEvents.triggerCustomEvent('chartSymbolChanged', {
                detail: { chartId: this.base.id, chartIndex: this.base.index, symbol: this.symbol }
            });
        }

        this.onSubscribeInitChartData();
    }

    public override requestNewTimeframe(timeframe: Timeframe) {
        const timeframeChange = this.timeframe != timeframe;
        if (!timeframeChange) {
            return;
        }

        this.timeframe = timeframe;

        const eventName = this.isPnP ? 'chartPipTimeFrameChanged' : 'chartTimeFrameChanged';
        const chartIndex = this.base.index;
        this.base.domEvents.triggerCustomEvent(eventName, {
            detail: { chartId: this.base.id, chartIndex: chartIndex, timeframe: ChartTimeFrameToTimeFrameMapper.map(timeframe) }
        });

        this.onSubscribeInitChartData();
    }

    public override requestSaveExtraFutureBarCount(extraBarCount: number) {
        if (this.isPnP || !this.saveRightAddPadding) {
            return;
        }

        return;
        // const key = this.getExtraFutureBarCountKeyStorage();
        // try {
        //     if (extraBarCount <= 0) {
        //         localStorage.removeItem(key);
        //     } else {
        //         localStorage.setItem(key, extraBarCount + '')
        //     }
        // } catch { }
    }

    public override requestCreatePriceAlert(model: CreateLimitAlertModel) {
        const createPriceAlert = new CreatePriceAlertRequestModel();
        createPriceAlert.symbol = model.symbol;
        createPriceAlert.price = model.price;
        createPriceAlert.isLong = model.isLong;

        createPriceAlert.afterHours = this.priceAlertAfterHours;

        this.priceAlertClient.createPriceAlert(createPriceAlert);
    }

    public override requestUpdateLimitAlert(id: string, price: number) {
        try {
            if (isNaN(price)) return;
            const requestedPrice = parseFloat(price.toFixed(2));
            const createPriceAlert = new CreatePriceAlertRequestModel();
            createPriceAlert.id = id;
            createPriceAlert.symbol = this.symbol;
            createPriceAlert.price = requestedPrice;
            createPriceAlert.isLong = this.lastPrice < requestedPrice;

            createPriceAlert.afterHours = this.priceAlertAfterHours;

            this.priceAlertClient.createPriceAlert(createPriceAlert);

            const existingTriggeredPriceAlertsKey = this.getPriceAlertTriggeredKeyStorage();
            const existingTriggeredPriceAlertsRaw = localStorage.getItem(existingTriggeredPriceAlertsKey);
            const existingTriggeredPriceAlerts = existingTriggeredPriceAlertsRaw ? JSON.parse(existingTriggeredPriceAlertsRaw) : [];
            const existingIdIndex = existingTriggeredPriceAlerts.findIndex(x => x == id);
            if (existingIdIndex > -1) {
                existingTriggeredPriceAlerts.splice(existingIdIndex, 1);
                localStorage.setItem(existingTriggeredPriceAlertsKey, JSON.stringify(existingTriggeredPriceAlerts));
            }
        } catch { }
    }

    public override requestRemoveLimitAlert(id: string) {
        // this.limitAlertManager.addRemovedId(id);
        this.priceAlertClient.deletePriceAlert(id);
    }

    private getExtraFutureBarCount(): number {
        const defaultPadding = 5;
        if (this.isPnP) {
            return defaultPadding;
        }

        return 0;
        // if (!this.saveRightAddPadding) {
        //     return this.rightEdgePaddingBarsDefault;
        // }

        // const key = this.getExtraFutureBarCountKeyStorage();
        // if (!key) {
        //     return this.rightEdgePaddingBarsDefault;
        // }

        // let count = this.rightEdgePaddingBarsDefault;
        // try {
        //     const result = localStorage.getItem(key);
        //     const val = parseFloat(result);
        //     if (val > 0) {
        //         count = val;
        //     }
        // } catch { }

        // TraceLoggingHelpers.log('getExtraFutureBarCount', [key, count])
        // return count;
    }

    private getExtraFutureBarCountKeyStorage(): string {
        const username = this.getUsernameKeyStorage();
        if (!username) {
            return '';
        }

        return `EBC_${username}`;
    }

    public override requestSetZoomLevel(clientZoomLevel: number) {
        if (this.base.pnpMode) {
            return;
        }


        const key = this.getZoomLevelKeyStorage();
        try {
            localStorage.setItem(key, clientZoomLevel + '')
        } catch { }
    }

    private getZoomLevel(): number | null {
        if (this.base.pnpMode) {
            return null;
        }

        try {
            const key = this.getZoomLevelKeyStorage();
            const result = localStorage.getItem(key);
            const val = parseFloat(result);
            if (val > 0) {
                return val;
            }
        } catch { }

        return null;
    }

    private getZoomLevelKeyStorage(): string {
        const username = this.getUsernameKeyStorage();
        if (!username) {
            return '';
        }

        return `MZL_${username}`;
    }

    public override requestSetPnPState(position: string, expanded: boolean, timeframe: Timeframe) {
        const key = this.getPIPKeyStorage();
        if (key) {
            if (!expanded) {
                localStorage.setItem(key, 'true');
            } else {
                localStorage.removeItem(key);
            }
        }
    }

    public override requestSaveDrawingTools(data: Array<any>) {
        try {
            const dataStr = JSON.stringify(data);

            localStorage.setItem(this.localStorageDrawingToolsKey, dataStr);
        }
        catch (ex: any) {
            console.error('requestSaveDrawingTools Error, ', ex);
        }
    }

    public override requestSaveDrawingToolSettings(data: DrawingToolsSettingModel) {
        try {
            const dataStr = JSON.stringify(data);

            localStorage.setItem(this.localStorageDrawingToolsSettings, dataStr);
        }
        catch (ex: any) {
            console.error('requestSaveDrawingToolSettings Error, ', ex);
        }
    }

    public override requestSaveHighChartIndicators(highChartIndicators: Array<IndicatorType>) {
        const key = this.getHighChartIndicatorsKeyStorage();
        try {
            const val = highChartIndicators.join(',');
            localStorage.setItem(key, val)
        } catch { }
    }

    private saveTimeframe() {
        try {
            const key = this.getTimeframeKeyStorage();
            localStorage.setItem(key, this.timeframe + '');
        }
        catch (ex: any) {
            console.error('saveTimeframe Error, ', ex);
        }
    }

    private getTimeframe(): number {
        try {
            const key = this.getTimeframeKeyStorage();
            const timeframe = parseInt(localStorage.getItem(key), 10);
            if (timeframe >= 0) {
                return timeframe;
            }
        }
        catch (ex: any) {
            console.error('saveTimeframe Error, ', ex);
        }

        return -1;
    }

    private getTimeframeKeyStorage(): string {
        return `TIMEFRAME${this.isPnP ? '_PIP' : ''}`;
    }

    private getHighChartIndicatorsKeyStorage(): string {
        const username = this.getUsernameKeyStorage();
        if (!username) {
            return '';
        }

        return `HCI_` + username;
    }

    private getSaveHighChartIndicators(): string {
        const key = this.getHighChartIndicatorsKeyStorage();
        if (this.isPnP || !key) {
            return '';
        }

        try {
            const result = localStorage.getItem(key);
            return result ? result : '';
        } catch { }

        return '';
    }

    public override requestSaveSingleStockWindowState(state: string) {
        const key = this.getSingleStockWindowStatesKeyStorage();
        try {
            localStorage.setItem(key, state)
        } catch { }
    }

    private getSingleStockWindowState(): string {
        const key = this.getSingleStockWindowStatesKeyStorage();
        if (!key) {
            return '';
        }

        try {
            const result = localStorage.getItem(key);
            return result ? result : '';
        } catch { }

        return '';
    }

    private getSingleStockWindowStatesKeyStorage(): string {
        return 'SSW_STATE';
    }

    private getPriceAlertTriggeredKeyStorage(): string {
        return 'PRICE_ALERT_TRIGGERED_' + this.username;
    }

    public override requestUpdateChartTrade(orderId: string, price: number, shares?: number) {
        const brokerOrder = this.brokerOrders.find((x: BrokerOrder) => x.OrderId == orderId);
        if (!brokerOrder) {
            return;
        }

        const tradeType = brokerOrder.TradeType?.toLowerCase();
        if (shares > 0 && brokerOrder.Shares != shares) {
            brokerOrder.Shares = shares;
        }

        if ((tradeType == 'limit' || tradeType == 'stoplimit') && price != brokerOrder.LimitPrice) {
            brokerOrder.LimitPrice = price;
            this.brokerManagerClient.ModifyOrder(brokerOrder);
        } else if ((tradeType == 'stop' || tradeType == 'stoplimit') && price != brokerOrder.StopPrice) {
            brokerOrder.StopPrice = price;
            this.brokerManagerClient.ModifyOrder(brokerOrder);
        }
    }

    public override requestUpdateChartTradeV2(orderId: string, price: number, shares?: number) {
        this.requestUpdateChartTrade(orderId, price, shares);
    }

    public override requestRemoveChartTrade(orderId: string) {
        TraceLoggingHelpers.log('cancelOrder: ', orderId);
        this.brokerManagerClient.CancelOrder(orderId);
    }

    public override requestRemoveChartTradeV2(orderId: string) {
        TraceLoggingHelpers.log('cancelOrder: ', orderId);
        this.brokerManagerClient.CancelOrder(orderId);
    }

    public override hasRemoveChartTradeV2(): boolean {
        return true;
    }

    public override requestManualTrade(model: TradeModel) {
        if (!this.brokerManagerClient) {
            return;
        }

        this.submitOrder(model.price, model.isBuy, model.orderType, model.quoteType, model.tif, model.account, model.quoteQuantity, model.ocaGroup);
    }

    public override requestInitiateChartTrade(price: number, isLong: boolean, quoteType?: string, account?: string, quoteQuantity?: number) {
        if (!this.brokerManagerClient) {
            return;
        }

        let orderType = TradeOrderType.Limit;

        const lastPrice = this.lastPrice;
        if (isLong) {
            if (price > lastPrice) {
                orderType = TradeOrderType.Stop;
            } else if (price <= lastPrice) {
                orderType = TradeOrderType.Limit;
            }
        } else {
            if (price < lastPrice) {
                orderType = TradeOrderType.Stop;
            }
            else if (price >= lastPrice) {
                orderType = TradeOrderType.Limit;
            }
        }

        this.submitOrder(price, isLong, orderType, quoteType, TradeInForceType.Day, account, quoteQuantity);
    }

    private submitOrder(
        price: number,
        isLong: boolean,
        orderType: TradeOrderType,
        quoteType: string,
        tif: TradeInForceType,
        accountName: string,
        quoteQuantity: number,
        ocaGroup?: string) {

        if (!this.brokerManagerClient) {
            return;
        }

        const account = this.accounts.find(x => x.name == accountName);
        if (!account) {
            return;
        }

        const brokerOrder = new BrokerOrder();
        brokerOrder.Side = isLong ? "Buy" : "Sell";

        if (orderType == TradeOrderType.Limit) {
            brokerOrder.TradeType = 'limit';
            brokerOrder.LimitPrice = price;
        } else if (orderType == TradeOrderType.Stop) {
            brokerOrder.TradeType = 'stop';
            brokerOrder.StopPrice = price;
        } else if (orderType == TradeOrderType.Market) {
            brokerOrder.TradeType = 'market';
        }

        if (tif == TradeInForceType.Week) {
            brokerOrder.GoodUntilTime = DateHelpers.addDays(new Date(), 7);
        }

        brokerOrder.AccountId = account.id + '';
        brokerOrder.Symbol = this.symbol;

        if (quoteType == QuoteType.Dollars) {
            brokerOrder.Shares = Math.floor(quoteQuantity / this.lastPrice);
        } else {
            brokerOrder.Shares = quoteQuantity;

        }

        if (ocaGroup) {
            brokerOrder.OcaGroup = ocaGroup;
        }

        this.setPnlComponentSelectedAccount(account.name);

        TraceLoggingHelpers.log('SubmitOrder: ', brokerOrder);
        this.brokerManagerClient.SubmitOrder(brokerOrder);
    }

    private setPnlComponentSelectedAccount(accountName: string) {
        const pnl = this.base.getComponentsByType(PnlComponent)[0] as PnlComponent;
        if (pnl) {
            pnl.selectedAccount = accountName;
        }
    }

    public override requestSaveTradeUIState(open: boolean, top: number | null, left: number | null, tif: string, account: string) {
        const key = this.getTradeUIStatesKeyStorage();
        try {
            localStorage.setItem(key, JSON.stringify({
                open: open,
                top: top,
                left: left,
                // quoteType: quoteType,
                // quoteQuantity: quoteQuantity,
                tif: tif,
                account: account,
            }))
        } catch { }
    }

    private getTradeUIState(): any {
        const key = this.getTradeUIStatesKeyStorage();
        if (!key) {
            return '';
        }

        try {
            const result = localStorage.getItem(key);
            return result ? JSON.parse(result) : '';
        } catch { }

        return '';
    }

    private getTradeUIStatesKeyStorage(): string {
        return 'TRADE_UI_STATE';
    }

    public override requestSaveEMACloudSettings(settings: EMACloudSettingsModel) {
        const key = this.getEMACloudSettingsKeyStorage();
        try {
            if (settings && settings.items?.length && settings.items[0].fast && settings.items[0].slow) {
                this.displayEMACloudIndicator = true;
                this.emaCloudSettings = settings;
                localStorage.setItem(key, JSON.stringify(settings));
            } else {
                this.displayEMACloudIndicator = false;
                this.emaCloudSettings = null;
                localStorage.removeItem(key);
            }

            this.onSubscribeInitChartData();
        } catch { }
    }

    private getEMACloudSettings() {
        if (this.chartMode != ChartMode.Standard || this.base.pnpMode) {
            this.displayEMACloudIndicator = false;
            this.emaCloudSettings = null;
            return;
        }

        const key = this.getEMACloudSettingsKeyStorage();

        try {
            const result = localStorage.getItem(key);
            const model = result ? JSON.parse(result) : null;
            if (model && model.items?.length && model.items[0].fast && model.items[0].slow) {
                this.displayEMACloudIndicator = true;

                const displayBuySellFlags = typeof model.displayBuySellFlags != 'undefined' ? model.displayBuySellFlags : true;
                const displayOnlyLastTwoTrades = typeof model.displayOnlyLastTwoTrades != 'undefined' ? model.displayOnlyLastTwoTrades : false;
                this.emaCloudSettings = new EMACloudSettingsModel({
                    displayBuySellFlags: displayBuySellFlags,
                    displayOnlyLastTwoTrades: displayOnlyLastTwoTrades,
                    sectionCount: this.emaCloudSectionCount
                });
                this.emaCloudSettings.items = [];
                for (let i = 0; i < model.items.length; i++) {
                    if (i > this.emaCloudSectionCount) {
                        break;
                    }

                    this.emaCloudSettings.items.push(new EMACloudSettingModel(model.items[i]));
                }
            } else {
                this.displayEMACloudIndicator = false;
                this.emaCloudSettings = null;
            }
        } catch { }
    }

    private getEMACloudSettingsKeyStorage(): string {
        return 'EMA_CLOUD_SETTINGS';
    }

    public override requestSaveChartSettingsPopupModel(model: ChartSettingsPopupModel) {
        const key = this.getChartSettingsPopupKeyStorage();
        try {
            if (model) {
                let shouldRequestNewData = false;

                if (model.displayPremarket != this.showPreMarket || model.displayPostmarket != this.showPostMarket) {
                    shouldRequestNewData = true;
                    this.showPreMarket = model.displayPremarket;
                    this.showPostMarket = model.displayPostmarket;
                }

                const jsonValue = JSON.stringify(model);
                localStorage.setItem(key, jsonValue);

                if (shouldRequestNewData) {
                    this.onSubscribeInitChartData();
                }
            }
        } catch { }
    }

    private getChartSettingsPopupModel(): ChartSettingsPopupModel {
        if (this.chartMode == ChartMode.Standard) {
            const key = this.getChartSettingsPopupKeyStorage();
            try {
                const item = localStorage.getItem(key);
                if (item) {
                    const m = JSON.parse(item);
                    if (typeof m.displayMainChartChangePercentVisibleCandles == 'undefined') {
                        m.displayMainChartChangePercentVisibleCandles = true;
                    }

                    return new ChartSettingsPopupModel(m);
                }
            } catch { }
        }

        const defaultModel = new ChartSettingsPopupModel();
        defaultModel.displayPremarket = this.showPreMarket;
        defaultModel.displayPostmarket = this.showPreMarket;
        defaultModel.defaultEnableTradeOrderEntry = this.newTradeUIEnabled;
        defaultModel.displayMainChartChangePercentVisibleCandles = true;

        return defaultModel;
    }

    private getChartSettingsPopupKeyStorage(): string {
        return 'CHART_SETTINGS_POPUP';
    }

    public override requestSaveIndicatorsForIndicatorManager(items: Array<ManageIndicatorModel>) {
        this.indicatorManager.requestSaveIndicatorsForIndicatorManager(items);
        this.onSubscribeInitChartData();
    }

    public override getSavedIndicatorsForIndicatorManager(): Array<ManageIndicatorModel> {
        if (this.chartMode != ChartMode.Standard) {
            return [];
        }

        const result = this.indicatorManager.getSavedIndicatorsForIndicatorManager(this.displayVolumeIndicator);
        return result;
    }
}
